// TS entry point for WASM-backed device operations, mirroring the
// CLI layer on top of the shared device library.
//
// Each op follows a fixed shape:
//   1. Load the current WasmDeviceRecord from IndexedDB.
//   2. Decrypt the seed envelope (and keyring envelope if present) via
//      the SharedWorker's non-extractable master key.
//   3. Call the matching WASM binding with (endpoint, seedHex,
//      keyringB64, ...op-specific args).
//   4. If the op grew the keyring, re-wrap and persist the updated
//      keyring back to IndexedDB.
//   5. Return the op result.
//
// Steps 1/2/4 are shared via `prepareDeviceArgs` + `persistUpdatedKeyring`.

import {
    loadCurrentWasmDevice,
    updateKeyringEnvelope,
    WasmDeviceRecord,
} from './deviceStore'
import { provisionSilentDevice } from './DeviceUnlockSetup'
import { deviceWorkerClient } from './deviceWorkerClient'
import { loadPracticemdWasm, type ReferenceData, type BookLink, type PracticeProfile, type NppesLookupResult, type ProviderPreview, type ProviderPreviewLink, type ProviderPreviewLinkMint } from './loader'
import type { AvaTurn, AvaSearchResult, AvaAdvanceResult, AvaMatchResult } from '../web-core/misc/useDevice.types'
import type {
    CreatedReferrer,
    ReferralClinicianDetail,
    ReferralClinicianFilter,
    ReferralClinicianList,
    ReferralPage,
    ReferralPair,
    ReferralPracticeMatrix,
    ReferralPracticeMatrixQuery,
    ReferralTargetList,
    ReferrerDetail,
    ReferrerInput,
    ReferrerList,
    ReferrerListFilter,
    ReferrerRecord,
    ReferrerTouchInput,
    ReferrerUpdate,
    ReferralShareAs,
    SharedReferralCode,
    MadeReferralCode,
    ReferrerAccountCandidate,
    ReferrerAccount,
} from './referralTypes'

// Gatsby's DefinePlugin substitutes `process.env.GATSBY_*` reads as
// inline string literals at build time, but does NOT define `process`
// itself in the browser bundle. A `typeof process !== 'undefined'`
// guard around the read evaluates to `false` in the browser and
// short-circuits to whatever fallback follows — quietly. The prior
// shape of this expression had that guard and was silently using the
// dev URL in the production bundle (broke prod login on first deploy
// after the WASM-ops rewrite). See ENG-49.
const defaultEndpoint = process.env.GATSBY_PRACTICEMD_API_ENDPOINT as string

function endpoint(): string {
    return defaultEndpoint
}

// Public accessor for the configured GraphQL endpoint. The push wiring
// passes this to connectPush, which derives the ws:// /socket URL from
// it (see DeriveWsEndpoint in the device library).
export function apiGraphqlEndpoint(): string {
    return defaultEndpoint
}

export class NoWasmDeviceError extends Error {
    constructor() {
        super('no upgraded device in this browser — visit /settings/device-security to set one up')
        this.name = 'NoWasmDeviceError'
    }
}

export class DeviceUnprotectedError extends Error {
    constructor() {
        super('This device has not been protected with a passphrase or passkey yet')
        this.name = 'DeviceUnprotectedError'
    }
}

export class DeviceLockedError extends Error {
    constructor() {
        super('device is locked — unlock it at /settings/device-security')
        this.name = 'DeviceLockedError'
    }
}

// Global re-unlock prompter. DeviceProvider registers a function at
// mount that shows a modal dialog and resolves when the user has
// successfully re-authenticated (or rejects if they cancel). When
// prepareDeviceArgs hits a locked SW and the prompter is registered,
// it awaits the prompter and then re-checks instead of throwing.
// Tests / non-React contexts leave the prompter unset and get the
// original throw-DeviceLockedError behavior.
let _unlockPrompter: (() => Promise<void>) | null = null
export function setUnlockPrompter(fn: (() => Promise<void>) | null): void {
    _unlockPrompter = fn
}

// Global setup prompter. Same shape as the unlock prompter: when
// prepareDeviceArgs finds no WasmDeviceRecord and a prompter is
// registered, it awaits the prompter (which surfaces the device
// setup ceremony) and re-checks before giving up. Tests / non-React
// contexts leave it unset and get the original throw-NoWasmDeviceError
// behavior.
let _setupPrompter: (() => Promise<void>) | null = null
export function setSetupPrompter(fn: (() => Promise<void>) | null): void {
    _setupPrompter = fn
}

// Ensure a WasmDeviceRecord exists in IndexedDB, surfacing the
// setup ceremony if not. No SW unlock — callers that only need the
// device's encKid (e.g. a forthcoming server call that will wrap a
// payload to it) shouldn't have to pay the unlock cost. Throws
// NoWasmDeviceError if the user dismisses the prompter without
// completing setup.
export async function ensureWasmDeviceProvisioned(): Promise<void> {
    let record = await loadCurrentWasmDevice()
    if (!record) {
        if (_setupPrompter) {
            await _setupPrompter()
            record = await loadCurrentWasmDevice()
        }
        if (!record) throw new NoWasmDeviceError()
    }
}

// Policy for prepareDeviceArgs (practicemd-api docs/Ava-Anonymous-Landing.md):
//   allowSilent      — no device yet: provision one SILENTLY (no dialog) instead
//                      of raising the setup prompter. Only the anonymous Ava
//                      conversation ops pass this; every other route keeps the
//                      first-use setup dialog.
//   requireProtected — the op is about to bind this device to an account
//                      (createUser, pairing, paper-key provisioning). An
//                      unprotected device is PROMOTED first via the setup
//                      prompter, whose UI detects the unprotected record and
//                      wraps the existing master key rather than minting a new
//                      device. Same device, same sigKid, same search.
export interface PrepareDeviceOptions {
    allowSilent?: boolean
    requireProtected?: boolean
}

// Prepares the seed + keyring the WASM bindings need. If no device
// is provisioned and a setup prompter is registered, delegates to
// it and re-checks once. Same dance for a locked SW with the unlock
// prompter. An unprotected (anonymous-landing) device unlocks itself
// from the clear master key on its record.
export async function prepareDeviceArgs(opts: PrepareDeviceOptions = {}): Promise<{
    seedHex: string
    keyringB64: string // "" if the keyring hasn't been pulled yet
    record: WasmDeviceRecord
}> {
    let record = await loadCurrentWasmDevice()
    if (!record) {
        if (opts.allowSilent) {
            record = await provisionSilentDevice()
        } else if (_setupPrompter) {
            await _setupPrompter()
            record = await loadCurrentWasmDevice()
        }
        if (!record) throw new NoWasmDeviceError()
    }

    if (opts.requireProtected && record.unprotectedMasterKeyHex) {
        if (_setupPrompter) {
            await _setupPrompter()
            record = (await loadCurrentWasmDevice()) || record
        }
        if (record.unprotectedMasterKeyHex) throw new DeviceUnprotectedError()
    }

    if (!(await deviceWorkerClient.isUnlocked()) && record.unprotectedMasterKeyHex) {
        await deviceWorkerClient.unlock(record.unprotectedMasterKeyHex)
    }

    if (!(await deviceWorkerClient.isUnlocked())) {
        if (_unlockPrompter) {
            await _unlockPrompter()
            if (!(await deviceWorkerClient.isUnlocked())) {
                throw new DeviceLockedError()
            }
        } else {
            throw new DeviceLockedError()
        }
    }

    const seedB64 = await deviceWorkerClient.decryptWithMasterKey(record.seedEnvelopeB64)
    const seedHex = base64ToHex(seedB64)

    const keyringB64 = record.keyringEnvelopeB64
        ? await deviceWorkerClient.decryptWithMasterKey(record.keyringEnvelopeB64)
        : ''

    return { seedHex, keyringB64, record }
}

// Called after any op that grew the keyring. Wraps the new keyring
// under the master key and writes it to IndexedDB.
export async function persistUpdatedKeyring(
    seedKid: string,
    keyringB64: string
): Promise<void> {
    const envelopeB64 = await deviceWorkerClient.encryptWithMasterKey(keyringB64)
    await updateKeyringEnvelope(seedKid, envelopeB64)
}

// --- Operations ---

// Decrypts an X25519 envelope (EncryptOutput shape, JSON-serialized
// as returned by the server) addressed to this device's encKid,
// using the device's own private key. Local-only — no server calls.
// Used by recovery flows (email-link) where a server-delivered
// envelope wraps a recovery device seed destined for this browser.
export async function decryptX25519Envelope(envelope: unknown): Promise<Uint8Array> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const envelopeJSON = typeof envelope === 'string' ? envelope : JSON.stringify(envelope)
    const plaintextB64 = await wasm.decryptX25519Envelope(seedHex, envelopeJSON)
    return base64ToUint8Array(plaintextB64)
}

// Pure deterministic derivation of device public kids from a 32-byte
// seed. No keyring, no persistence, no server calls. Used by the
// logout confirmation flow to verify a user-entered paper-key mnemonic
// matches a registered paper-key device before a destructive action.
export async function deviceKidsForSeed(seedHex: string): Promise<{
    seedKid: string
    sigKid: string
    encKid: string
    aesKid: string
}> {
    const wasm = await loadPracticemdWasm()
    return await wasm.deviceKidsForSeed(seedHex)
}

// --- Device-AES encrypt / decrypt ------------------------------------
//
// Wire-shape translation: Go's crypto.EncryptOutput serializes with
// short JSON keys (kid / ct / dkct / dkid / alg / kalg / sender_pk /
// recipient_pk / nonce). The TS EncryptOutput type uses long keys
// (publicKeyId / ciphertext / dataKeyCiphertext / dataKeyId /
// algorithm / keyAlgorithm / senderPublicKey / recipientPublicKey /
// nonce). Existing stored blobs (pre-migration, written by the legacy
// encryptWithSecureAES path) are long-key TS form — so we translate
// at the boundary and keep on-disk format stable.
function shortToLongEO(s: any): any {
    if (!s || typeof s !== 'object') return s
    // already long-key form? pass through
    if ('ciphertext' in s || 'algorithm' in s) return s
    return {
        publicKeyId: s.kid,
        dataKeyCiphertext: s.dkct ?? s.dk,
        ciphertext: s.ct,
        dataKeyId: s.dkid,
        algorithm: s.alg,
        keyAlgorithm: s.kalg,
        senderPublicKey: s.sender_pk,
        recipientPublicKey: s.recipient_pk,
        nonce: s.nonce,
    }
}

function longToShortEO(l: any): any {
    if (!l || typeof l !== 'object') return l
    // already short-key form? pass through
    if ('ct' in l || 'alg' in l) return l
    return {
        kid: l.publicKeyId,
        dkct: l.dataKeyCiphertext,
        ct: l.ciphertext,
        dkid: l.dataKeyId,
        alg: l.algorithm,
        kalg: l.keyAlgorithm,
        sender_pk: l.senderPublicKey,
        recipient_pk: l.recipientPublicKey,
        nonce: l.nonce,
    }
}

// Encrypt plaintext with this device's AES-256 key. Local-only. The
// returned value is in the long-key TS EncryptOutput shape so it can
// be stored as-is (matches the legacy encryptWithSecureAES wire form).
export async function encryptWithDeviceAes(plaintext: Uint8Array): Promise<any> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const plaintextB64 = uint8ArrayToBase64(plaintext)
    const r = await wasm.encryptWithDeviceAes(seedHex, plaintextB64)
    if (!r.success) throw new Error(r.error || 'encryptWithDeviceAes failed')
    return shortToLongEO(JSON.parse(r.encryptedJSON))
}

// Decrypt an EncryptOutput-shaped item using any key available to the
// device (its own keys, already-synced distributed keys, or keys
// derivable from PxK distributions on demand). Accepts either Go
// short-key or TS long-key form. Returns plaintext bytes, or null on
// any failure. If the local keyring is stale, the WASM binding does an
// auto-pull-on-miss and retries once before reporting failure — same
// pattern as decryptMessage. If decryption populated new keys, persists
// the updated keyring envelope.
export async function decryptEncryptedItem(encryptedItem: any): Promise<Uint8Array | null> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const shortForm = longToShortEO(encryptedItem)
    const r = await wasm.decryptEncryptedItem(endpoint(), seedHex, keyringB64, JSON.stringify(shortForm))
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) return null
    return base64ToUint8Array(r.plaintextB64)
}

// Recipient for encryptMessage. Mirrors device.MessageRecipient — caller
// resolves emails/userIds/teamIds to (subjectId, encKid) before calling.
export interface MessageRecipient {
    subjectId: string
    encKid: string
    permissions?: string[]
    conditions?: {
        accessMode?: string
        requiredEmail?: string
        expiryDate?: string
    }
}

// Build a portable, multi-recipient EncryptedMessage envelope (same
// content-identity + per-recipient-grant pattern as sends, but suitable
// for offline transport). Wire format aligns across platforms: a value
// produced here decrypts via the CLI's `crypto decrypt` and any other
// device.DecryptMessage caller. ENG-9.
export async function encryptMessage(plaintext: Uint8Array, recipients: MessageRecipient[]): Promise<string> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const plaintextB64 = uint8ArrayToBase64(plaintext)
    const recipientsJSON = JSON.stringify(recipients.map(r => ({
        SubjectId: r.subjectId,
        EncKid: r.encKid,
        Permissions: r.permissions,
        Conditions: r.conditions ? {
            AccessMode: r.conditions.accessMode,
            RequiredEmail: r.conditions.requiredEmail,
            ExpiryDate: r.conditions.expiryDate,
        } : undefined,
    })))
    const r = await wasm.encryptMessage(seedHex, keyringB64, plaintextB64, recipientsJSON)
    if (!r.success) throw new Error(r.error || 'encryptMessage failed')
    return r.messageJSON as string
}

// Decrypt an EncryptedMessage envelope (typed as a JSON string of
// device.EncryptedMessage). If the local keyring is stale relative to
// the recipient grants in the envelope, the WASM binding auto-pulls
// fresh distributions and retries once before reporting failure.
// Returns the plaintext bytes; throws on any failure (no usable
// recipient after pull, expired grant, parse error). If the decrypt
// populated new keys, persists the updated keyring envelope. ENG-9.
export async function decryptMessage(messageJSON: string): Promise<Uint8Array> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.decryptMessage(endpoint(), seedHex, keyringB64, messageJSON)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) throw new Error(r.error || 'decryptMessage failed')
    return base64ToUint8Array(r.plaintextB64)
}

export interface PullKeyringResult {
    added: number
    skipped: number
    total: number
}

// Mirrors the CLI's `keyring pull` command. Fetches the caller device's
// PxK distributions from the server, syncs them into the stored
// keyring, and persists the result. Safe to run repeatedly — the
// server only returns distributions this device hasn't seen yet.
export async function pullKeyring(): Promise<PullKeyringResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const result = await wasm.pullKeyring(endpoint(), seedHex, keyringB64)
    if (result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    return { added: result.added, skipped: result.skipped, total: result.total }
}

// --- WASM keyring introspection (dev tooling) ---------------------

// Shape of one entry under ClientKeyring.DistributedKeys. Matches Go's
// device.DistributedKey JSON tags.
export interface WasmKeyringDistributedKey {
    kid: string
    keyType: string // "sig" | "enc" | "aes" | "seed"
    key: string // base64; not rendered in UI
}

// Shape of one entry under ClientKeyring.PxKDistributions. Matches Go's
// device.PxKDistributionInfo JSON tags.
export interface WasmKeyringPxKDistribution {
    identityId: string
    identityType: string
    encryptedSeed: unknown
    seedKid: string
    sigKid: string
    encKid: string
    aesKid: string
}

export interface WasmKeyringSnapshot {
    deviceKids: {
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
    }
    distributedKeys: Record<string, WasmKeyringDistributedKey>
    pxkDistributions: Record<string, WasmKeyringPxKDistribution>
}

// Loads the current device's persisted WASM keyring from IDB and
// returns its parsed contents. Returns an empty snapshot if nothing
// has been pulled yet. Intended for the dev Developer page; not for
// hot-path decrypts (which go through Go directly).
export async function getWasmKeyringSnapshot(): Promise<WasmKeyringSnapshot> {
    const { keyringB64, record } = await prepareDeviceArgs()
    const deviceKids = {
        seedKid: record.seedKid,
        sigKid: record.sigKid,
        encKid: record.encKid,
        aesKid: record.aesKid,
    }
    if (!keyringB64) {
        return { deviceKids, distributedKeys: {}, pxkDistributions: {} }
    }
    let jsonBytes: Uint8Array
    try {
        jsonBytes = base64ToUint8Array(keyringB64)
    } catch (e) {
        throw new Error(`getWasmKeyringSnapshot: failed to decode keyring base64: ${e instanceof Error ? e.message : String(e)}`)
    }
    const jsonStr = new TextDecoder().decode(jsonBytes)
    const parsed = JSON.parse(jsonStr) as Partial<WasmKeyringSnapshot>
    return {
        deviceKids,
        distributedKeys: parsed.distributedKeys || {},
        pxkDistributions: parsed.pxkDistributions || {},
    }
}

// Pair and authorize flows hit a brief race where this device's
// signature is valid but its user_devices row hasn't propagated yet;
// getMyPxKDistributions returns [] in that window and a single
// pullKeyring silently produces an empty sync. Retry with backoff
// until we see keyring contents or exhaust attempts. Throws if it
// never lands — callers should surface that as a user-visible error.
// Only use this from flows that KNOW distributions should exist
// immediately (post-pairing, post-authorize); regular polling/refresh
// should continue to use plain pullKeyring.
export async function pullKeyringForProvisioning(): Promise<void> {
    const delaysMs = [0, 750, 1500, 3000, 5000]
    let lastErr: unknown = null
    for (let i = 0; i < delaysMs.length; i++) {
        if (delaysMs[i] > 0) {
            await new Promise(resolve => setTimeout(resolve, delaysMs[i]))
        }
        try {
            const r = await pullKeyring()
            if ((r.total ?? 0) > 0) return
        } catch (err) {
            lastErr = err
        }
    }
    throw new Error(
        `pullKeyring failed to sync any distributions after ${delaysMs.length} attempts` +
        (lastErr ? `: ${lastErr instanceof Error ? lastErr.message : String(lastErr)}` : '')
    )
}

// Heartbeat response. User fields are re-keyed into the snake_case
// shape UserInfo uses elsewhere on the site — the WASM binding emits
// camelCase (givenName / familyName / emailVerified); consumers
// expect given_name / family_name / email_verified.
export interface HeartbeatResult {
    success: boolean
    error?: string
    lastSeenAt?: string
    serverTime?: string
    timeSinceLastSeen?: string
    user?: {
        id: string
        name?: string
        given_name?: string
        family_name?: string
        email?: string
        email_verified?: boolean
        nickname?: string
        sigKid?: string
        encKid?: string
    }
}

export async function sendHeartbeat(): Promise<HeartbeatResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.heartbeat(endpoint(), seedHex)
    const u = r.user
    return {
        success: !!r.success,
        error: r.error || undefined,
        lastSeenAt: r.lastSeenAt,
        serverTime: r.serverTime,
        timeSinceLastSeen: r.timeSinceLastSeen,
        user: u ? {
            id: u.id,
            name: u.name,
            given_name: u.givenName,
            family_name: u.familyName,
            email: u.email,
            email_verified: u.emailVerified,
            nickname: u.nickname,
            sigKid: u.sigKid,
            encKid: u.encKid,
        } : undefined,
    }
}

// Result type matches the legacy ReadSaveResult in src/api/saveHelpers.ts
// so consumers can swap call sites without reshaping their handling.
export interface ReadSaveOpResult {
    success: boolean
    id?: string
    identityId?: string
    itemType?: string
    data?: Uint8Array
    metadata?: Record<string, any>
    authorSigKid?: string
    createdAt?: Date
    error?: string
}

// E2E save read. Mirrors the CLI's `saves read` path: fetch encrypted
// envelope, decrypt via the keyring, return plaintext + metadata. If
// the read grew the keyring (on-demand PxK derivation for a previously
// unseen identity key), the updated keyring is persisted back under
// the master key.
export async function readSave(saveId: string): Promise<ReadSaveOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const result = await wasm.readSave(endpoint(), seedHex, keyringB64, saveId)
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    if (!result.success) {
        return { success: false, error: result.error || 'readSave failed' }
    }
    return {
        success: true,
        id: result.id || undefined,
        identityId: result.identityId || undefined,
        itemType: result.itemType || undefined,
        data: base64ToUint8Array(result.dataB64),
        metadata: result.metadata || undefined,
        authorSigKid: result.authorSigKid || undefined,
        createdAt: result.createdAt ? new Date(result.createdAt) : undefined,
    }
}

export interface ListSavesArgs {
    identityId: string
    itemType: string
    metadata?: Record<string, any>
    pageSize?: number
    cursor?: string
}

export interface SaveSummary {
    id: string
    identityId: string
    itemType: string
    metadata: Record<string, any> | null
    authorSigKid: string
    createdAt: string
}

export interface ListSavesOpResult {
    items: SaveSummary[]
    nextCursor: string
    totalCount: number
}

// Metadata-only list of saves for an identity + itemType. Mirrors the
// CLI's `saves list` command: device.ListSaves(callerDevice, backend,
// args) over the WASM transport. No payload decryption.
export async function listSaves(args: ListSavesArgs): Promise<ListSavesOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const argsForWasm = {
        identityId: args.identityId,
        itemType: args.itemType,
        metadata: args.metadata,
        pageSize: args.pageSize || 0,
        cursor: args.cursor || '',
    }
    const result = await wasm.listSaves(endpoint(), seedHex, JSON.stringify(argsForWasm))
    return {
        items: result.items,
        nextCursor: result.nextCursor,
        totalCount: result.totalCount,
    }
}

export interface RotatePukOpResult {
    success: boolean
    error?: string
    newPukInfo?: { seedKid: string; sigKid: string; encKid: string; aesKid: string }
    devicesTotal?: number
    distributionSuccesses?: number
    distributionFailures?: number
    distributionErrors?: string[]
}

// Client-orchestrated user PUK rotation. Generates a new PUK, announces
// the new identity, installs the PUK into the local keyring, and
// redistributes to sibling devices. The keyring always grows (the new
// PUK is installed here), so the updated keyring is persisted under
// the master key on the way out.
export async function rotatePuk(): Promise<RotatePukOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const result = await wasm.rotatePuk(endpoint(), seedHex, keyringB64)
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    if (!result.success) {
        return {
            success: false,
            error: (result.distributionErrors && result.distributionErrors[0]) || 'rotatePuk failed',
            devicesTotal: result.devicesTotal,
            distributionSuccesses: result.distributionSuccesses,
            distributionFailures: result.distributionFailures,
            distributionErrors: result.distributionErrors,
        }
    }
    return {
        success: true,
        newPukInfo: {
            seedKid: result.newSeedKid,
            sigKid: result.newSigKid,
            encKid: result.newEncKid,
            aesKid: result.newAesKid,
        },
        devicesTotal: result.devicesTotal,
        distributionSuccesses: result.distributionSuccesses,
        distributionFailures: result.distributionFailures,
        distributionErrors: result.distributionErrors,
    }
}

export interface DeleteSaveOpResult {
    success: boolean
    error?: string
}

// Server-side soft-delete of a save by id. No client-side crypto.
export async function deleteSave(saveId: string): Promise<DeleteSaveOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const result = await wasm.deleteSave(endpoint(), seedHex, saveId)
    if (!result.success) {
        return { success: false, error: result.error || 'deleteSave failed' }
    }
    return { success: true }
}

export interface WriteSaveOpResult {
    success: boolean
    saveId?: string
    error?: string
}

// E2E save write. Mirrors the CLI's `saves create` path: encrypt
// plaintext to the target identity's AES key, sign + POST. The
// keyring may grow during target-identity lookup (on-demand PxK
// derivation); the updated keyring is persisted back under the
// master key when it differs from the cached one.
export async function writeSave(
    identityId: string,
    itemType: string,
    plaintext: Uint8Array,
    metadata?: Record<string, any> | null,
    existingId?: string
): Promise<WriteSaveOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const plaintextB64 = uint8ArrayToBase64(plaintext)
    const metadataJSON = metadata ? JSON.stringify(metadata) : ''
    const result = await wasm.writeSave(
        endpoint(),
        seedHex,
        keyringB64,
        identityId,
        itemType,
        plaintextB64,
        metadataJSON,
        existingId || ''
    )
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    if (!result.success) {
        return { success: false, error: result.error || 'writeSave failed' }
    }
    return { success: true, saveId: result.saveId || undefined }
}

// ---- Sends ----

export interface IdentityInfo {
    id: string
    seedKid: string
    sigKid: string
    encKid: string
    aesKid: string
}

// Resolve a recipient's public key material by email, user id, or kid.
// Public query; no signature. Used by writeSend to turn a recipient
// user id into the encKid needed for content-seed encryption.
export async function getIdentity(key: string): Promise<IdentityInfo> {
    const wasm = await loadPracticemdWasm()
    return wasm.getIdentity(endpoint(), key, await cacheSeedHex())
}

// Positional, by-kid batch form of getIdentity (at most 200 kids). Public
// query; no signature. Element i is the identity for kids[i] or null.
export async function getIdentities(kids: string[]): Promise<Array<IdentityInfo | null>> {
    if (kids.length === 0) return []
    const wasm = await loadPracticemdWasm()
    return wasm.getIdentities(endpoint(), JSON.stringify(kids), await cacheSeedHex())
}

// The current device's seed for the shared library's sealed cache tier
// (identity_cache.go), or '' when there is no device or it is locked.
// Never prompts: identity lookups happen before setup and while locked
// too, and a memory-only cache is the right answer there.
async function cacheSeedHex(): Promise<string> {
    try {
        const record = await loadCurrentWasmDevice()
        if (!record || !(await deviceWorkerClient.isUnlocked())) return ''
        return base64ToHex(await deviceWorkerClient.decryptWithMasterKey(record.seedEnvelopeB64))
    } catch {
        return ''
    }
}

export interface WriteSendOpResult {
    success: boolean
    sendId?: string
    ephemeralFragments?: Record<string, string>
    error?: string
}

// RecipientSpec mirrors the Go device.RecipientSpec shape consumed by the
// WASM writeSend binding. Local to ops.ts; consumers import the public
// interface from useDevice.types.ts.
export interface OpsRecipientSpec {
    mode: 'known' | 'bearer' | 'email_verified'
    identityId?: string
    encKid?: string
    email?: string
    recipientName?: string
}

// Write an E2E send to a single known recipient identified by user id.
// Resolves the recipient's encKid via getIdentity, then hands to the
// WASM op. Matches the legacy Device.writeSend signature so existing
// consumers don't change.
export async function writeSend(
    recipientId: string,
    itemType: string,
    plaintext: Uint8Array,
    metadata?: Record<string, any> | null
): Promise<WriteSendOpResult> {
    return writeSendMulti(
        [{ mode: 'known', identityId: recipientId }],
        itemType,
        plaintext,
        metadata
    )
}

// Write an E2E send to multiple recipients in a single operation. For
// mode='known' entries missing encKid, resolves via getIdentity before
// handing to the WASM op. The WASM binding already supports
// bearer/email_verified modes; this wrapper just forwards them through.
export async function writeSendMulti(
    recipients: OpsRecipientSpec[],
    itemType: string,
    plaintext: Uint8Array,
    metadata?: Record<string, any> | null
): Promise<WriteSendOpResult> {
    if (!recipients || recipients.length === 0) {
        return { success: false, error: 'writeSendMulti: at least one recipient is required' }
    }
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()

    // Resolve encKid for any known recipients that are missing it. The
    // Go-side field names are PascalCase (Mode/EncKid/IdentityId/Email),
    // per encoding/json tag defaults in device.RecipientSpec.
    const resolved: Array<{ Mode: string; EncKid?: string; IdentityId?: string; Email?: string }> = []
    for (const r of recipients) {
        if (r.mode === 'known') {
            let encKid = r.encKid
            let identityId = r.identityId
            if (!encKid && identityId) {
                const identity = await wasm.getIdentity(endpoint(), identityId)
                if (!identity.encKid) {
                    return { success: false, error: `identity ${identityId} has no encKid` }
                }
                encKid = identity.encKid
                identityId = identity.id
            }
            if (!encKid) {
                return { success: false, error: 'known recipient missing identityId/encKid' }
            }
            resolved.push({ Mode: 'known', EncKid: encKid, IdentityId: identityId })
        } else if (r.mode === 'email_verified') {
            // email_verified grants MUST be bound to an email — the
            // server uses it to gate reads behind the verification
            // code. Bearer-without-email is allowed (anonymous
            // bearer: the fragment alone grants access).
            if (!r.email) {
                return { success: false, error: 'email_verified recipient missing email' }
            }
            resolved.push({ Mode: 'email_verified', Email: r.email })
        } else {
            // mode === 'bearer'. Email is optional; Go keys the fragment
            // by the ephemeral seedKid when no email is supplied.
            const payload: { Mode: string; Email?: string } = { Mode: 'bearer' }
            if (r.email) payload.Email = r.email
            resolved.push(payload)
        }
    }

    const result = await wasm.writeSend(
        endpoint(),
        seedHex,
        keyringB64,
        itemType,
        uint8ArrayToBase64(plaintext),
        JSON.stringify(resolved),
        metadata ? JSON.stringify(metadata) : ''
    )
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    if (!result.success) {
        return { success: false, error: result.error || 'writeSend failed' }
    }
    return {
        success: true,
        sendId: result.sendId,
        ephemeralFragments: result.ephemeralFragments,
    }
}

// Write an E2E send with NO user identity — the anonymous form submission
// path. The device is minted SILENTLY if this browser has none (the Ava
// anonymous-landing behavior: no setup dialog; a later signup PROMOTES the
// same device, preserving its keys, which is what lets the submitter claim
// the submission into their new account). The server admits the send only
// through the writeSendV2 anonymous gate (opted-in claimed template, rate
// limits), so callers should surface result.error to the user verbatim.
export async function writeSendAnonymous(
    recipientId: string,
    itemType: string,
    plaintext: Uint8Array,
    metadata?: Record<string, any> | null
): Promise<WriteSendOpResult> {
    if (!recipientId) {
        return { success: false, error: 'writeSendAnonymous: recipient is required' }
    }
    const { seedHex, keyringB64, record } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()

    const identity = await wasm.getIdentity(endpoint(), recipientId)
    if (!identity?.encKid) {
        return { success: false, error: `identity ${recipientId} has no encKid` }
    }

    const result = await wasm.writeSendAnonymous(
        endpoint(),
        seedHex,
        keyringB64,
        itemType,
        uint8ArrayToBase64(plaintext),
        JSON.stringify([{ Mode: 'known', EncKid: identity.encKid, IdentityId: identity.id }]),
        metadata ? JSON.stringify(metadata) : ''
    )
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    if (!result.success) {
        return { success: false, error: result.error || 'writeSendAnonymous failed' }
    }
    return { success: true, sendId: result.sendId }
}

// Post-signup: bind an anonymous submission this device made to the new
// account (re-wrap content seed to the user PUK; server records the
// role=sender grant after verifying this device authored the send).
export async function bindAnonymousFormSubmission(
    sendId: string,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const result = await wasm.bindAnonymousFormSubmission(endpoint(), seedHex, keyringB64, sendId)
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    return { success: result.success, error: result.error || undefined }
}

export interface ReadSendOpResult {
    success: boolean
    id?: string
    itemType?: string
    createdAt?: string
    metadata?: Record<string, any>
    data?: Uint8Array
    error?: string
    emailVerificationRequired?: boolean
}

// Session-scoped throwaway seed for the no-account visitor path. The
// server binds 24h verified-access state to the caller's sigKid, so
// readSendAnonymous + requestEphemeralEmailVerification +
// verifyEphemeralAccess + the retry read all need to sign with the
// same key. Generated lazily on first use; persists for the lifetime
// of this JS module (page load). A page reload gets a fresh identity.
let anonymousSeedHex: string | null = null
function getAnonymousSeedHex(): string {
    if (anonymousSeedHex) return anonymousSeedHex
    const seed = new Uint8Array(32)
    crypto.getRandomValues(seed)
    anonymousSeedHex = uint8ArrayToHex(seed)
    return anonymousSeedHex
}

// Compute the ephemeralIdentityId the server uses to key a bearer/
// email_verified grant: sha256(seed) as hex, where seed is the
// base64url-decoded URL fragment. Mirrors
// crypto.NewEphemeralKeyMaterialFromSeed(seed).SeedKid on the Go side.
export async function ephemeralIdentityIdFromFragment(fragment: string): Promise<string> {
    const seed = base64urlToUint8Array(fragment)
    const hash = await crypto.subtle.digest('SHA-256', seed)
    return uint8ArrayToHex(new Uint8Array(hash))
}

// Unauthenticated bearer-send read. No device, no signin — just a URL
// fragment and a sendId. The WASM binding signs with a session-stable
// throwaway so verify→retry flows (email_verified) work. This is the
// no-account visitor flow: a user clicks a shared link and sees the
// content.
//
// Deliberately doesn't call prepareDeviceArgs — no Service Worker
// unlock, no IndexedDB device, no keyring.
export async function readSendAnonymous(
    sendId: string,
    fragment: string
): Promise<ReadSendOpResult> {
    const wasm = await loadPracticemdWasm()
    const result = await wasm.readSendAnonymous(endpoint(), getAnonymousSeedHex(), sendId, fragment)
    if (!result.success) {
        return {
            success: false,
            error: result.error || 'readSendAnonymous failed',
            emailVerificationRequired: result.emailVerificationRequired,
        }
    }
    return {
        success: true,
        id: result.id,
        itemType: result.itemType,
        createdAt: result.createdAt,
        metadata: result.metadata || undefined,
        data: base64ToUint8Array(result.dataB64),
    }
}

// --- Provisioning (authorizer + pairing requester) ----------------------

export interface ProvisionWithAuthorizerOpResult {
    success: boolean
    error?: string
    deviceId?: string
    /** Populated keyring for the newly-provisioned device (base64). */
    keyringB64?: string
}

// Paper-key-login flow. authorizerSeed is the seed derived from a
// paper-key mnemonic; newDeviceSeed is a freshly-generated seed for
// whatever device is joining the user. The shared library signs the
// provisioning request with the authorizer, redistributes its PxK
// material to the current browser device, registers it, and syncs
// the keyring. Caller's device seed comes from prepareDeviceArgs
// (the already-persisted WasmDeviceRecord); only the authorizer
// seed is external (paper-key mnemonic, email-delivered recovery
// seed, etc.). The updated keyring is persisted to IDB before we
// return, so a subsequent pullKeyring can walk the chain from it.
export async function provisionWithAuthorizer(
    authorizerSeedHex: string,
    deviceName: string,
    deviceType: 'regular' | 'paper' | 'passkey' = 'regular',
): Promise<ProvisionWithAuthorizerOpResult> {
    // A cold browser gets a SILENT device here and the flow's protect step
    // runs AFTER provisioning (Appendix C, C3): the passkey it creates is an
    // account passkey, which needs the account's email and a signed-in
    // device to register. It used to require protection first, which raised
    // the generic setup dialog and minted a device-only passkey.
    const { seedHex, record } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.provisionWithAuthorizer(
        endpoint(),
        seedHex,
        authorizerSeedHex,
        deviceName,
        deviceType,
    )
    if (!r.success) return { success: false, error: r.error || 'provisionWithAuthorizer failed' }
    if (r.keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return { success: true, deviceId: r.deviceId, keyringB64: r.keyringB64 }
}

export interface ProvisionWithPairingOpResult {
    success: boolean
    error?: string
    pairingCode?: string
    pairingId?: string
    expiresIn?: number
}

// Pairing-requester flow. Returns the short pairingCode to show the
// user, and a pairingId to poll via checkPairingStatus. Once the
// approver completes pairing server-side, the caller syncs by
// calling pullKeyring — both checkPairingStatus and pullKeyring now
// use the already-persisted device seed via prepareDeviceArgs, so
// there are no *ForSeed variants to coordinate.
export async function provisionWithPairing(
    // Optional: the sole caller passes `deviceName || undefined` when the
    // field is blank, so undefined has always been reachable here. Typing it
    // as required was simply wrong, not a constraint anything relied on.
    deviceName?: string,
): Promise<ProvisionWithPairingOpResult> {
    // Silent on a cold browser; the flow protects after approval (C3).
    const { seedHex } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.provisionWithPairing(endpoint(), seedHex, deviceName)
    if (!r.success) return { success: false, error: r.error || 'provisionWithPairing failed' }
    return {
        success: true,
        pairingCode: r.pairingCode,
        pairingId: r.pairingId,
        expiresIn: r.expiresIn,
    }
}

// --- Payment authorizations + getSavedForm ------------------------------

export interface IntendToAddPaymentAuthorizationOpResult {
    available: boolean
    hasAgreementOnFile: boolean
    hasCardOnFile: boolean
    error?: Record<string, any>
    formTemplate?: Record<string, any>
    formData?: Record<string, any>
    providerInfo?: Record<string, any>
    cardSummary?: {
        last4?: string
        cardBrand?: string
        cardholderName?: string
        expMonth?: number
        expYear?: number
    }
}

// Pre-capture handshake for adding a payment authorization. Either
// practice (the provider's user/team id) or integrationId must be
// non-empty. Server-side no-decrypt query — the payload is all
// unencrypted metadata. Server-side "not found" errors live in the
// returned object's `error` field rather than thrown.
export async function intendToAddPaymentAuthorization(
    opts: { practice?: string; integrationId?: string },
): Promise<IntendToAddPaymentAuthorizationOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.intendToAddPaymentAuthorization(
        endpoint(),
        seedHex,
        opts.practice || '',
        opts.integrationId || '',
    )
    return r as IntendToAddPaymentAuthorizationOpResult
}

// Fetches a rendered payment-authorization PDF by id. Returns the
// server's base64 string; the caller converts to a Blob.
export async function getPaymentAuthorizationPdf(id: string): Promise<string> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.getPaymentAuthorizationPdf(endpoint(), seedHex, id)
}

export interface AddPaymentMethodOpArgs {
    practice?: string
    integrationId?: string
    paymentMethod?: string
    billingPostalCode?: string
    formData?: Record<string, any>
}

export interface AddPaymentMethodOpResult {
    error?: Record<string, any>
    card?: {
        last4: string
        cardBrand: string
        expMonth: number
        expYear: number
        cardholderName: string
    }
    hasAgreementOnFile: boolean
    hasCardOnFile: boolean
    vendorInfo?: Record<string, any>
}

// Pre-emptively registers an ephemeral identity (from a URL fragment)
// into the device's keyring so future reads of the corresponding send
// don't need the fragment. Best-effort: ReadSend also auto-stores when
// called with a fragment. Keyring may grow; persists updated envelope.
export async function storeEphemeralIdentity(fragment: string): Promise<void> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.storeEphemeralIdentity(endpoint(), seedHex, keyringB64, fragment)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) {
        throw new Error(r.error || 'storeEphemeralIdentity failed')
    }
}

// Attaches a card (via a vendor-provided nonce) and/or submits an
// agreement (via formData) against an integration or legacy practice.
// Server-side "not found"/validation errors live in the returned
// object's `error` field rather than thrown.
export async function addPaymentMethod(
    args: AddPaymentMethodOpArgs,
): Promise<AddPaymentMethodOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const payload = {
        Practice: args.practice || '',
        IntegrationId: args.integrationId || '',
        PaymentMethod: args.paymentMethod || '',
        BillingPostalCode: args.billingPostalCode || '',
        FormData: args.formData ? JSON.stringify(args.formData) : '',
    }
    return await wasm.addPaymentMethod(endpoint(), seedHex, JSON.stringify(payload))
}

export interface GetSavedFormOpResult {
    success: boolean
    error?: string
    save?: Record<string, any>
}

// Fetch a form-submission save by its form id. Payload is E2E
// encrypted; caller decrypts locally (same shape as readSaveE2E —
// just keyed by form id rather than save id). Keyring may grow
// server-side during identity resolution, so we persist whatever
// comes back.
export async function getSavedForm(formId: string): Promise<GetSavedFormOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.getSavedForm(endpoint(), seedHex, keyringB64, formId)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) {
        return { success: false, error: r.error || 'getSavedForm failed' }
    }
    return { success: true, save: r.save }
}

// --- Payment vendors + billing ------------------------------------------
//
// Greenfield additions, not currently consumed by any existing page.
// Return shapes are untyped `Record<string, any>` because common.*
// Go types are rich and the server contract is the source of truth;
// consumers will narrow the shape as they wire these into UI.

export async function listPaymentVendors(identityId: string): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listPaymentVendors(endpoint(), seedHex, identityId)
    return r.items
}

export async function getPaymentVendor(
    opts: { integrationId?: string; identityId?: string; vendorType?: string },
): Promise<Record<string, any> | null> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.getPaymentVendor(
        endpoint(),
        seedHex,
        opts.integrationId || '',
        opts.identityId || '',
        opts.vendorType || '',
    )
    return r.vendor || null
}

export async function deletePaymentVendor(
    identityId: string,
    opts: { integrationId?: string; vendorType?: string },
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.deletePaymentVendor(
        endpoint(),
        seedHex,
        identityId,
        opts.integrationId || '',
        opts.vendorType || '',
    )
    return { success: r.success, error: r.error || undefined }
}

export async function validatePaymentVendor(
    identityId: string,
    integrationId: string,
): Promise<{ success: boolean; error?: string; merchantId?: string; businessName?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.validatePaymentVendor(endpoint(), seedHex, identityId, integrationId)
    return {
        success: r.success,
        error: r.error || undefined,
        merchantId: r.merchantId || undefined,
        businessName: r.businessName || undefined,
    }
}

export async function createSquareOAuthUrl(
    identityId: string,
    integrationId?: string,
): Promise<{ success: boolean; error?: string; authorizationUrl?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.createSquareOAuthUrl(endpoint(), seedHex, identityId, integrationId || '')
    return {
        success: r.success,
        error: r.error || undefined,
        authorizationUrl: r.authorizationUrl || undefined,
    }
}

export async function completeSquareOAuth(
    code: string,
    state: string,
): Promise<{ success: boolean; error?: string; vendor?: Record<string, any> }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.completeSquareOAuth(endpoint(), seedHex, code, state)
    return { success: r.success, error: r.error || undefined, vendor: r.vendor }
}

export async function listPlans(includeInactive = false): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listPlans(endpoint(), seedHex, includeInactive)
    return r.items
}

export async function getMyLicenses(activeOnly = false): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.getMyLicenses(endpoint(), seedHex, activeOnly)
    return r.items
}

export async function getMyUsage(subscriptionId: string): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.getMyUsage(endpoint(), seedHex, subscriptionId)
}

export async function subscribeMe(args: {
    planId: string
    billingInterval: string
    paymentMethodId?: string
}): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.subscribeMe(endpoint(), seedHex, JSON.stringify(args))
}

export async function changeSubscriptionPlan(
    subscriptionId: string,
    newPlanId: string,
    billingInterval = '',
): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.changeSubscriptionPlan(endpoint(), seedHex, subscriptionId, newPlanId, billingInterval)
}

export async function cancelSubscription(
    subscriptionId: string,
    immediate = false,
): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.cancelSubscription(endpoint(), seedHex, subscriptionId, immediate)
}

export async function reactivateSubscription(subscriptionId: string): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.reactivateSubscription(endpoint(), seedHex, subscriptionId)
}

// --- Form templates ------------------------------------------------------
//
// All form-template Device methods are new surface; existing consumers
// (FormDesigner, dashboard, etc.) continue to hit raw GraphQL helpers
// and will migrate incrementally. Inputs/outputs shaped loosely as
// Record<string, any> because the underlying common.Template Go type
// is rich and we don't want to parrot every field in TypeScript when
// the caller will just pass it through to the server anyway.

export interface CreateFormTemplateOpResult {
    success: boolean
    error?: string
    claimed: boolean
    limitExceeded: boolean
    limitMessage: string
    upgradeCTA: string
    template?: Record<string, any>
}

export async function createFormTemplate(
    template: Record<string, any>,
    opts?: { createForIdentityId?: string; selectedSubscriptionId?: string },
): Promise<CreateFormTemplateOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.createFormTemplate(
        endpoint(),
        seedHex,
        JSON.stringify(template),
        opts?.createForIdentityId || '',
        opts?.selectedSubscriptionId || '',
    )
    return {
        success: r.success,
        error: r.error || undefined,
        claimed: r.claimed,
        limitExceeded: r.limitExceeded,
        limitMessage: r.limitMessage,
        upgradeCTA: r.upgradeCTA,
        template: r.template,
    }
}

export async function updateFormTemplate(template: Record<string, any>): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateFormTemplate(endpoint(), seedHex, JSON.stringify(template))
    return { success: r.success, error: r.error || undefined }
}

export async function updateFormTemplateName(id: string, name: string): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateFormTemplateName(endpoint(), seedHex, id, name)
    return { success: r.success, error: r.error || undefined }
}

export async function updateFormTemplateMarks(
    id: string,
    marks: Array<Record<string, any>>,
    presubmitConditions?: Array<Record<string, any>>,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateFormTemplateMarks(
        endpoint(),
        seedHex,
        id,
        JSON.stringify(marks),
        presubmitConditions ? JSON.stringify(presubmitConditions) : '',
    )
    return { success: r.success, error: r.error || undefined }
}

export async function updateFormTemplatePDF(
    id: string,
    pdf: { sha256?: string; bytes?: string },
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateFormTemplatePDF(endpoint(), seedHex, id, JSON.stringify(pdf))
    return { success: r.success, error: r.error || undefined }
}

export async function updateFormTemplateRecipient(
    id: string,
    recipientUserId: string,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateFormTemplateRecipient(endpoint(), seedHex, id, recipientUserId)
    return { success: r.success, error: r.error || undefined }
}

// Owner-only: opt a CLAIMED template into (or out of) anonymous no-login
// form submissions. The server refuses unclaimed templates.
export async function updateFormTemplateAllowAnonymous(
    id: string,
    allow: boolean,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateFormTemplateAllowAnonymous(endpoint(), seedHex, id, allow)
    return { success: r.success, error: r.error || undefined }
}

export async function deleteFormTemplate(
    id: string,
    opts?: { force?: boolean },
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.deleteFormTemplate(endpoint(), seedHex, id, !!opts?.force)
    return { success: r.success, error: r.error || undefined }
}

export async function claimFormTemplate(args: {
    id: string
    claimForIdentityId?: string
    allowTransfer?: boolean
    selectedSubscriptionId?: string
}): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.claimFormTemplate(endpoint(), seedHex, JSON.stringify(args))
    return { success: r.success, error: r.error || undefined }
}

export interface ListFormTemplatesArgs {
    id?: string
    contentHash?: string
    createdByUserId?: string
    createdByCurrentUser?: boolean
    ownedByCurrentUser?: boolean
    ownedByIdentityId?: string
    pageSize?: number
    cursor?: string
}

export interface ListFormTemplatesOpResult {
    items: Array<Record<string, any>>
    nextCursor: string
    totalCount: number
    error?: string
}

export async function listFormTemplates(args: ListFormTemplatesArgs = {}): Promise<ListFormTemplatesOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listFormTemplates(endpoint(), seedHex, JSON.stringify(args))
    return {
        items: r.items,
        nextCursor: r.nextCursor,
        totalCount: r.totalCount,
        error: r.error || undefined,
    }
}

export async function readFormTemplatePdf(
    opts: { sha256?: string; templateId?: string },
): Promise<{ success: boolean; error?: string; data?: Uint8Array }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.readFormTemplatePdf(
        endpoint(),
        seedHex,
        opts.sha256 || '',
        opts.templateId || '',
    )
    if (!r.success) return { success: false, error: r.error || 'readFormTemplatePdf failed' }
    return { success: true, data: base64ToUint8Array(r.pdfB64) }
}

export async function assignPaymentIntegrationToTemplate(
    templateId: string,
    paymentIntegrationId?: string | null,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.assignPaymentIntegrationToTemplate(
        endpoint(),
        seedHex,
        templateId,
        paymentIntegrationId || '',
    )
    return { success: r.success, error: r.error || undefined }
}

export interface OpsPermission {
    type: string
    allowed?: boolean
    limit?: number
}

export interface TeamInfo {
    id: string
    name: string
    createdAt: string
    myPermissions: OpsPermission[]
}

export interface TeamMember {
    userId: string
    name: string
    email: string
    permissions: OpsPermission[]
}

export interface CreateTeamOpResult {
    success: boolean
    error?: string
    teamId?: string
    name?: string
}

// Create a new team. Runs device.CreateTeam — generates a PTK,
// announces the team, distributes the PTK to the owner, and stores
// it in the caller's keyring. We persist the updated keyring on the
// way out so other sessions/devices see the new PTK after pull.
export async function createTeam(teamName: string): Promise<CreateTeamOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.createTeam(endpoint(), seedHex, keyringB64, teamName)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) return { success: false, error: r.error || 'createTeam failed' }
    return { success: true, teamId: r.teamId, name: r.name }
}

// Add a user to a team. userIdOrEmail is resolved by the server.
// Permissions is the initial access list. Keyring is read-only here
// (we decrypt the team PTK to re-encrypt for the new member).
export async function addUserToTeam(
    teamId: string,
    userIdOrEmail: string,
    permissions: OpsPermission[],
): Promise<{ success: boolean; error?: string }> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.addUserToTeam(
        endpoint(),
        seedHex,
        keyringB64,
        teamId,
        userIdOrEmail,
        JSON.stringify(permissions),
    )
    return { success: r.success, error: r.error || undefined }
}

// Replace a member's entire permissions list. Direct backend call.
export async function updateMemberPermissions(
    teamId: string,
    userId: string,
    permissions: OpsPermission[],
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateMemberPermissions(
        endpoint(),
        seedHex,
        teamId,
        userId,
        JSON.stringify(permissions),
    )
    return { success: r.success, error: r.error || undefined }
}

// Remove a user from a team with full PTK rotation. Announces a new
// team identity, distributes the new PTK to remaining members, then
// revokes the grant. Persists the caller's updated keyring.
export async function removeMemberFromTeam(
    teamId: string,
    userIdOrEmail: string,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.removeMemberFromTeam(endpoint(), seedHex, keyringB64, teamId, userIdOrEmail)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return { success: r.success, error: r.error || undefined }
}

// Delete a team. Caller must have ownership permission.
export async function deleteTeam(teamId: string): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.deleteTeam(endpoint(), seedHex, teamId)
    return { success: r.success, error: r.error || undefined }
}

// List teams the authenticated user is a member of.
export async function listTeams(): Promise<TeamInfo[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listTeams(endpoint(), seedHex)
    return r.items
}

// List members of a team.
export async function listTeamMembers(teamId: string): Promise<TeamMember[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listTeamMembers(endpoint(), seedHex, teamId)
    return r.items
}

// --- Team membership via consent (in-band messages) ---

export interface PendingIbm {
    id: string
    category: string
    body: string
    state: string
    createdAt: number
    expiresAt: number
    dismissedAt: number
    ackAt: number
}

// One pending notification for the center. anyDeviceAck = per-user read aggregate
// (any of the recipient's/team's devices acked/dismissed it). refTable/refId = the
// deep-link target.
export interface NotificationCenterItem {
    id: string
    category: string
    body: string
    refTable: string
    refId: string
    state: string
    seq: number
    createdAt: number
    expiresAt: number
    anyDeviceAck: boolean
}

// Invite an existing user to a team. Pre-wraps the team PTK to the invitee's
// encKid and stores a pending invitation (an IBM). No membership until accept.
export async function inviteExistingUserToTeam(
    teamId: string,
    inviteeIdOrEmail: string,
    permissions: OpsPermission[],
): Promise<{ success: boolean; invitationId?: string; itemId?: string; error?: string }> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.inviteExistingUserToTeam(
        endpoint(),
        seedHex,
        keyringB64,
        teamId,
        inviteeIdOrEmail,
        JSON.stringify(permissions),
    )
    return {
        success: r.success,
        invitationId: r.invitationId || undefined,
        itemId: r.itemId || undefined,
        error: r.error || undefined,
    }
}

// Accept a team invitation by its IBM item id. The server commits the
// pre-wrapped distribution and creates the grant; we then pull the keyring so
// the team key materializes locally.
export async function acceptTeamInvitation(
    itemId: string,
): Promise<{ success: boolean; teamId?: string; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.acceptTeamInvitation(endpoint(), seedHex, itemId)
    if (r.success) {
        // Materialize the newly-granted team PTK into the local keyring. A
        // failure here is non-fatal: a later pull (reconnect / refresh) recovers.
        try {
            await pullKeyring()
        } catch {
            /* non-fatal */
        }
    }
    return { success: r.success, teamId: r.teamId || undefined, error: r.error || undefined }
}

// Decline a team invitation by its IBM item id.
export async function declineTeamInvitation(
    itemId: string,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.declineTeamInvitation(endpoint(), seedHex, itemId)
    return { success: r.success, error: r.error || undefined }
}

// Admin: cancel a still-pending invitation by its pending_membership id.
export async function withdrawTeamInvitation(
    pendingId: string,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.withdrawTeamInvitation(endpoint(), seedHex, pendingId)
    return { success: r.success, error: r.error || undefined }
}

// Request to join a team (the request direction). Nothing is wrapped — the
// requester holds no team key — so no keyring is needed; an admin wraps and
// commits on approve.
export async function requestToJoinTeam(
    teamId: string,
    message: string,
): Promise<{ success: boolean; requestId?: string; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.requestToJoinTeam(endpoint(), seedHex, teamId, message)
    return { success: r.success, requestId: r.requestId || undefined, error: r.error || undefined }
}

// Admin: approve a pending join request. Wraps the team PTK to the requester's
// encKid (needs the keyring to decrypt the PTK) and the server commits the
// distribution + grant. The requester materializes the key via their own pull.
export async function approveJoinRequest(
    pendingId: string,
    teamId: string,
    requesterUserId: string,
): Promise<{ success: boolean; teamId?: string; error?: string }> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.approveJoinRequest(
        endpoint(),
        seedHex,
        keyringB64,
        pendingId,
        teamId,
        requesterUserId,
    )
    return { success: r.success, teamId: r.teamId || undefined, error: r.error || undefined }
}

// Admin: deny a pending join request by its pending_membership id. No rotation.
export async function denyJoinRequest(
    pendingId: string,
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.denyJoinRequest(endpoint(), seedHex, pendingId)
    return { success: r.success, error: r.error || undefined }
}

// Invite an email with no account (the non-user path). Wraps the team PTK to a
// fresh ephemeral key and emails the access link (tempSk in the fragment) via
// the client-scoped SES rail. siteUrl is the origin used to build the link.
export async function inviteEmailToTeam(
    teamId: string,
    inviteeEmail: string,
    siteUrl: string,
    permissions: OpsPermission[],
): Promise<{ success: boolean; invitationId?: string; accessUrl?: string; emailError?: string; error?: string }> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.inviteEmailToTeam(
        endpoint(),
        seedHex,
        keyringB64,
        teamId,
        inviteeEmail,
        siteUrl,
        JSON.stringify(permissions),
    )
    return {
        success: r.success,
        invitationId: r.invitationId || undefined,
        accessUrl: r.accessUrl || undefined,
        emailError: r.emailError || undefined,
        error: r.error || undefined,
    }
}

// Claim a non-user invitation from its link (pendingId + fragment). Decrypts the
// team PTK with the ephemeral key, re-wraps to the caller's own PUK, and joins;
// pulls the keyring so the team key materializes locally.
export async function claimEmailInvitation(
    pendingId: string,
    fragment: string,
): Promise<{ success: boolean; teamId?: string; error?: string }> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.claimEmailInvitation(endpoint(), seedHex, keyringB64, pendingId, fragment)
    if (r.success) {
        try {
            await pullKeyring()
        } catch {
            /* non-fatal: a later pull recovers */
        }
    }
    return { success: r.success, teamId: r.teamId || undefined, error: r.error || undefined }
}

// List this device's live in-band messages (the inbox).
export async function listPendingIbms(): Promise<PendingIbm[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listPendingIbms(endpoint(), seedHex)
    return r.items
}

// The user's pending notifications for the center (user + team recipient), each
// with the per-user anyDeviceAck read flag.
export async function notificationCenterItems(): Promise<NotificationCenterItem[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.notificationCenterItems(endpoint(), seedHex)
    return r.items
}

// The per-user NEEDS YOU unread badge count.
export async function notificationUnreadCount(): Promise<number> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.notificationUnreadCount(endpoint(), seedHex)
    return r.count
}

// Record this device's ack+dismiss on a notification (plain dismiss, no verdict —
// never settles globally); flips the per-user anyDeviceAck so it reads as read in
// the center. Used for ack-on-open of terminal/informational items.
export async function dismissStateItem(itemId: string): Promise<void> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    await wasm.dismissStateItem(endpoint(), seedHex, itemId)
}

export interface TeamPendingInvitation {
    pendingId: string
    inviteeUserId: string
    inviteeEmail: string
    inviteeName: string
    permissions: string[]
    createdAt: number
    expiresAt: number
}

// Admin view: a team's pending (unaccepted) invitations (display metadata only).
export async function listTeamPendingInvitations(
    teamId: string,
): Promise<TeamPendingInvitation[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listTeamPendingInvitations(endpoint(), seedHex, teamId)
    return r.items
}

export interface TeamJoinRequest {
    pendingId: string
    requesterUserId: string
    requesterEmail: string
    requesterName: string
    message: string
    createdAt: number
    expiresAt: number
}

// Admin view: a team's pending (unapproved) join requests (display metadata only).
export async function listTeamJoinRequests(
    teamId: string,
): Promise<TeamJoinRequest[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listTeamJoinRequests(endpoint(), seedHex, teamId)
    return r.items
}

// Admin auto-handler: perform any pending team-PTK rotations this device is
// responsible for (the ptk-rotation-required backstop). Persists the updated
// keyring, since rotation re-keys the team and grows the local keyring.
export async function handlePtkRotations(): Promise<{ handled: number; error?: string }> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.handlePtkRotations(endpoint(), seedHex, keyringB64)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return { handled: r.handled, error: r.error || undefined }
}

export interface PushFrameOutcome {
    action: string
    reason: string
    keyringChanged: boolean
    userInfo: Record<string, any> | null
    rotationsHandled: number
    error?: string
}

// Route ONE inbound push frame through the shared policy.
//
// Serialized against every other call here: each one round-trips keyringB64,
// so two frames handled concurrently could each import the pre-update keyring
// and the second write would lose the first's rotation. Frames arrive in
// bursts, so this is not theoretical — the previous per-frame
// `void dev.pullKeyring()` had exactly that race.
let pushQueue: Promise<unknown> = Promise.resolve()

export async function consumePushFrame(
    selfIdentityId: string,
    frame: unknown
): Promise<PushFrameOutcome> {
    const run = pushQueue.then(async () => {
        const { seedHex, keyringB64, record } = await prepareDeviceArgs()
        const wasm = await loadPracticemdWasm()
        const r = await wasm.consumePushFrame(
            endpoint(),
            seedHex,
            keyringB64,
            selfIdentityId || "",
            typeof frame === "string" ? frame : JSON.stringify(frame ?? {})
        )
        if (r.keyringChanged && r.keyringB64 && r.keyringB64 !== keyringB64) {
            await persistUpdatedKeyring(record.seedKid, r.keyringB64)
        }
        return {
            action: r.action,
            reason: r.reason,
            keyringChanged: r.keyringChanged,
            userInfo: r.userInfo,
            rotationsHandled: r.rotationsHandled,
            error: r.error || undefined,
        }
    })
    // Keep the chain alive even when a link rejects, or one bad frame would
    // wedge every later frame behind a permanently rejected promise.
    pushQueue = run.catch(() => undefined)
    return run
}

export interface CatchUpOutcome {
    pulledKeyring: boolean
    keyringChanged: boolean
    userInfo: Record<string, any> | null
    rotationsHandled: number
    error?: string
}

// Reconnect convergence (§16.5). Shares the queue with consumePushFrame for
// the same reason: it round-trips the keyring too.
export async function catchUpAfterConnect(): Promise<CatchUpOutcome> {
    const run = pushQueue.then(async () => {
        const { seedHex, keyringB64, record } = await prepareDeviceArgs()
        const wasm = await loadPracticemdWasm()
        const r = await wasm.catchUpAfterConnect(endpoint(), seedHex, keyringB64)
        if (r.keyringChanged && r.keyringB64 && r.keyringB64 !== keyringB64) {
            await persistUpdatedKeyring(record.seedKid, r.keyringB64)
        }
        return {
            pulledKeyring: r.pulledKeyring,
            keyringChanged: r.keyringChanged,
            userInfo: r.userInfo,
            rotationsHandled: r.rotationsHandled,
            error: r.error || undefined,
        }
    })
    pushQueue = run.catch(() => undefined)
    return run
}

export interface PairingStatusOpResult {
    status: string
    isCompleted: boolean
    expiresIn: number
    error?: string
}

// Called on the NEW device, polling for approval. Uses the
// persisted device's seed — the server requires the poll signer's
// sigKid to match the sigKid registered with the pairing request,
// which is the current device's sigKid after provisionWithPairing.
// Callers in an anonymous context must mint a persisted device via
// DeviceUnlockSetup before calling this.
export async function checkPairingStatus(pairingId: string): Promise<PairingStatusOpResult> {
    try {
        const { seedHex } = await prepareDeviceArgs()
        const wasm = await loadPracticemdWasm()
        const r = await wasm.checkPairingStatus(endpoint(), seedHex, pairingId)
        return { status: r.status, isCompleted: r.isCompleted, expiresIn: r.expiresIn }
    } catch (e) {
        return {
            status: '',
            isCompleted: false,
            expiresIn: 0,
            error: e instanceof Error ? e.message : String(e),
        }
    }
}

export interface ApprovePairingOpResult {
    success: boolean
    error?: string
    deviceName?: string
}

// Called on the AUTHENTICATED (approver) device. Runs the full
// ceremony via device.ApprovePairing — GetPairingRequest →
// FetchDistributions + sync → InsertDistribution per PxK →
// ProvisionDevice(new) → CompleteDevicePairing. Signed with the
// approver's device seed; persist the updated keyring on the way out.
export async function approvePairing(
    pairingCode: string,
    deviceName?: string,
): Promise<ApprovePairingOpResult> {
    try {
        const { seedHex, keyringB64, record } = await prepareDeviceArgs()
        const wasm = await loadPracticemdWasm()
        const r = await wasm.approvePairing(
            endpoint(),
            seedHex,
            keyringB64,
            pairingCode,
            deviceName || '',
        )
        if (r.keyringB64 && r.keyringB64 !== keyringB64) {
            await persistUpdatedKeyring(record.seedKid, r.keyringB64)
        }
        if (!r.success) return { success: false, error: r.error || 'approvePairing failed' }
        return { success: true, deviceName: r.deviceName }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

export interface RemoteDeviceInfo {
    id: string
    name: string
    deviceType: string
    sigKid: string
    encKid: string
    createdAt: string
    lastSeenAt: string
    isCurrentDevice: boolean
    online: boolean
    pxkStatus?: {
        totalPresent: number
        totalInUniverse: number
        isComplete: boolean
        missingPxks: Array<{ identityId: string; identityType: string; seedKid: string }>
    }
}

// Enumerate all devices registered for the authenticated user.
// Metadata only; no crypto.
export async function listDevices(): Promise<RemoteDeviceInfo[]> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listDevices(endpoint(), seedHex, keyringB64)
    return r.items
}

// Revoke one or more devices owned by the caller. Runs the full
// ceremony — new PUK, distribute to remaining, revoke, sync — via
// device.RevokeDevices. Keyring grows (new PUK lands in the caller's
// keyring); we persist it back to IndexedDB before returning.
//
// The server prevents a device from revoking itself; that validation
// also runs client-side in device.RevokeDevices.
export async function revokeDevices(
    sigKids: string[],
    onProgress?: (ev: { step: string; message: string; current: number; total: number }) => void,
): Promise<{ success: boolean; error?: string }> {
    if (!sigKids || sigKids.length === 0) {
        return { success: false, error: 'revokeDevices: at least one sigKid required' }
    }
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.revokeDevices(endpoint(), seedHex, keyringB64, JSON.stringify(sigKids), onProgress)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) {
        return { success: false, error: r.error || 'revokeDevices failed' }
    }
    return { success: true }
}

// pushKeys mirrors the CLI's `practicemd keyring push`: this device
// re-encrypts its existing PxK distributions for every *other* device
// on the account. Use-case is the inverse of pullKeyring — when the
// server is missing distributions for a peer device but this device
// has the plaintext. Ceremony runs server-side; keyring may grow on
// the caller during the FetchDistributions sync step, so we persist
// any updated keyring back before returning.
export async function pushKeys(
    onProgress?: (ev: { step: string; message: string; current: number; total: number }) => void,
): Promise<{ success: boolean; error?: string; distributed?: number }> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.pushKeys(endpoint(), seedHex, keyringB64, onProgress)
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) {
        return { success: false, error: r.error || 'pushKeys failed', distributed: 0 }
    }
    return { success: true, distributed: r.distributed ?? 0 }
}

// Output of createUser — the caller passes the whole thing back to
// completeUserCreation unchanged. Carries the PUK seed generated
// server-side during phase 1 AND the device seed this page generated
// locally to sign the createUser request. Both are needed by phase 2.
export interface PendingAccount {
    success: boolean
    verificationSent?: boolean
    error?: string
    userId?: string
    email?: string
    pukSeedHex?: string
    /** Device seed from the local WasmDeviceRecord — same one used for
     *  every signed op. Carried through phase 2 so completeUserCreation
     *  signs with the same identity. Don't log. */
    deviceSeedHex?: string
    /** Device keyring (base64 envelope) after completeUserCreation. */
    keyringB64?: string
}

export interface CompleteUserCreationOpResult {
    success: boolean
    error?: string
    userId?: string
    /** Device seed — persist after success to make the signup "stick". */
    deviceSeedHex?: string
    /** Device keyring populated with the user's PUK distribution. */
    keyringB64?: string
}

// Phase 1: initiate account creation. Signs the createUser mutation
// with this browser's device seed (loaded from the WasmDeviceRecord
// that the provider's setup prompter ensured exists). Returns a
// PendingAccount carrying the device seed for phase 2 and the PUK
// seed for envelope ops.
//
// Invariant: a WasmDeviceRecord must exist and the SW must be unlocked
// before signup — signing with a fresh random seed here would register
// a server-side device whose seed IndexedDB doesn't hold, breaking
// every subsequent signed op (registerPaperKey, etc.).
//
// Release 1: allowSilent, not requireProtected. Nothing binds the device to
// an account here — that is finalize. A browser with no device yet gets
// the SILENT one (the anonymous /ava landing's shape: a clear master key on
// the record, promoted later, same seed, same sigKid); the PASSKEY step
// between this call and the code then protects it under the passkey, so
// one biometric does both jobs. finalizeUserCreation requires protection,
// so a guest who declined the passkey meets the setup prompter there,
// once. Prompting for protection here made the ceremony ask twice:
// passphrase now, Face ID next.
export async function createUser(
    userId: string,
    email: string,
    deviceName?: string,
): Promise<PendingAccount> {
    try {
        const { seedHex } = await prepareDeviceArgs({ allowSilent: true })
        const wasm = await loadPracticemdWasm()
        const r = await wasm.createUser(endpoint(), seedHex, userId, email, deviceName || '')
        if (!r.success) {
            return {
                success: false,
                verificationSent: r.verificationSent,
                error: r.error || 'createUser failed',
            }
        }
        return {
            success: true,
            verificationSent: r.verificationSent,
            userId: r.userId,
            email: r.email,
            pukSeedHex: r.pukSeedHex,
            deviceSeedHex: seedHex,
        }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

// Phase 2: submit the verification code and finalize account
// creation. pending is the PendingAccount returned from createUser.
// Signs with the SAME device seed used in phase 1 (pending.deviceSeedHex)
// so the server's stored pending state matches. On success the device
// keyring now holds the user's PUK distribution; returns it along
// with the device seed so the caller can persist both into
// IndexedDB (under the user's master key / passphrase envelope).
export async function completeUserCreation(
    pending: PendingAccount,
    code: string,
): Promise<CompleteUserCreationOpResult> {
    if (!pending.userId || !pending.pukSeedHex || !pending.deviceSeedHex) {
        return { success: false, error: 'completeUserCreation: pending state missing required fields' }
    }
    try {
        const wasm = await loadPracticemdWasm()
        const r = await wasm.completeUserCreation(
            endpoint(),
            pending.deviceSeedHex,
            pending.userId,
            pending.pukSeedHex,
            code,
        )
        if (!r.success) {
            return { success: false, error: r.error || 'completeUserCreation failed' }
        }
        return {
            success: true,
            userId: r.userId,
            deviceSeedHex: pending.deviceSeedHex,
            keyringB64: r.keyringB64,
        }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

// ENG-81 pre-ceremony gate: check the emailed code with the server
// WITHOUT committing anything, so a mistyped code fails before the user
// sits through the paper-key ceremony. finalizeUserCreation re-verifies —
// this is UX, not the commit gate.
export async function verifyCreationCode(
    pending: PendingAccount,
    code: string,
): Promise<{ success: boolean; error?: string }> {
    if (!pending.userId || !pending.deviceSeedHex) {
        return { success: false, error: 'verifyCreationCode: pending state missing required fields' }
    }
    try {
        const wasm = await loadPracticemdWasm()
        const r = await wasm.verifyCreationCode(endpoint(), pending.deviceSeedHex, pending.userId, code)
        return r.success ? { success: true } : { success: false, error: r.error || 'verification failed' }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

// ENG-81: the single commit point of account creation. Called only after
// the user has proven possession of the paper key locally. The server
// commits user + device + paper key + both PUK distributions in one
// transaction — abandon anywhere before this call and nothing exists.
// Same result shape as completeUserCreation so callers persist the same
// way.
// One entry of the RECOVERY GROUP finalizeUserCreation registers with the
// account (Release 1): a paper key the person just transcribed, or a
// passkey recovery device the browser just minted. seedHex is the entry's
// prepared seed (preparePaperKey / prepareRecoveryDevice); the passkey
// fields come from enrollRecoveryPasskey and wrapRecoverySeed.
export interface RecoveryGroupEntry {
    kind: 'paper' | 'passkey'
    seedHex: string
    name?: string
    credentialId?: string
    publicKey?: string
    envelope?: string
}

export async function finalizeUserCreation(
    pending: PendingAccount,
    code: string,
    recovery: RecoveryGroupEntry[],
): Promise<CompleteUserCreationOpResult> {
    if (!pending.userId || !pending.pukSeedHex || !pending.deviceSeedHex) {
        return { success: false, error: 'finalizeUserCreation: pending state missing required fields' }
    }
    for (const e of recovery) {
        if (!e.seedHex) return { success: false, error: `finalizeUserCreation: ${e.kind} entry has no seed` }
        if (e.kind === 'passkey' && !(e.credentialId && e.publicKey && e.envelope)) {
            return { success: false, error: 'finalizeUserCreation: passkey entry is incomplete' }
        }
    }
    try {
        // The binding moment. A passkey sign-up protected the device in the
        // passkey step; a guest who declined keeps the SILENT device — no
        // second prompt (Appendix C, owner ruling 2026-09-22: a person who
        // can do a passkey and declines proceeds with no local lock; the
        // in-thread ask or "Add Passkey" locks the browser later). Only a
        // browser that CANNOT do a passkey gets a passphrase, and that is the
        // protect step's business, not finalize's.
        await prepareDeviceArgs({ allowSilent: true })
        const wasm = await loadPracticemdWasm()
        // An empty group commits a patient GUEST (R1/R3).
        const r = await wasm.finalizeUserCreation(
            endpoint(),
            pending.deviceSeedHex,
            pending.userId,
            pending.pukSeedHex,
            code,
            JSON.stringify(recovery.map(e => ({
                kind: e.kind, seedHex: e.seedHex, name: e.name || '',
                credentialId: e.credentialId || '', publicKey: e.publicKey || '', envelope: e.envelope || '',
            }))),
        )
        if (!r.success) {
            return { success: false, error: r.error || 'finalizeUserCreation failed' }
        }
        return {
            success: true,
            userId: r.userId,
            deviceSeedHex: pending.deviceSeedHex,
            keyringB64: r.keyringB64,
        }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

// ENG-81 attestation: prove possession of a paper key from its 24 words.
// The words are converted to a seed locally and the derived device SIGNS the
// proof request — the mnemonic never leaves the page. Success means the
// server stamped the key's proven_at and settled any pending attestation
// notifications.
export async function provePaperKeyWithWords(
    words: string,
): Promise<{ success: boolean; provenAt?: string; error?: string }> {
    try {
        const wasm = await loadPracticemdWasm()
        const derived = await wasm.mnemonicToSeed(words)
        if (!derived.success) {
            return { success: false, error: 'That does not look like a valid 24-word paper key.' }
        }
        const r = await wasm.provePaperKeyPossession(endpoint(), derived.seedHex)
        if (!r.success) {
            return { success: false, error: r.error || 'Proof failed' }
        }
        return { success: true, provenAt: r.provenAt }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

// Output of preparePaperKey. Carries the seedHex the caller passes
// back to registerPaperKey once the user has confirmed offline
// preservation of the mnemonic. The split exists to prevent the
// "server thinks a paper key exists but the user never saved it"
// data-loss failure mode.
export interface PreparedPaperKeyOpResult {
    success: boolean
    error?: string
    seedHex?: string
    mnemonic?: string[]
    seedKid?: string
    sigKid?: string
    encKid?: string
    aesKid?: string
}

export interface ProvisionPaperKeyOpResult {
    success: boolean
    error?: string
    mnemonic?: string[]
    seedKid?: string
    sigKid?: string
    encKid?: string
    aesKid?: string
}

// Generate a paper-key seed locally and derive the mnemonic + device
// kids. No server contact. Returned seedHex must be passed to
// registerPaperKey to complete registration.
export async function preparePaperKey(): Promise<PreparedPaperKeyOpResult> {
    try {
        const wasm = await loadPracticemdWasm()
        const r = await wasm.preparePaperKey()
        return {
            success: true,
            seedHex: r.seedHex,
            mnemonic: r.mnemonic,
            seedKid: r.seedKid,
            sigKid: r.sigKid,
            encKid: r.encKid,
            aesKid: r.aesKid,
        }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

// Register a previously-prepared paper key. prepared.seedHex is the
// local seed; the keyring may grow during FetchDistributions → sync.
export async function registerPaperKey(
    prepared: PreparedPaperKeyOpResult,
    paperKeyName?: string,
): Promise<ProvisionPaperKeyOpResult> {
    if (!prepared.seedHex) {
        return { success: false, error: 'registerPaperKey: prepared.seedHex missing' }
    }
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.registerPaperKey(
        endpoint(),
        seedHex,
        keyringB64,
        prepared.seedHex,
        paperKeyName || '',
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) {
        return { success: false, error: r.error || 'registerPaperKey failed' }
    }
    return {
        success: true,
        mnemonic: prepared.mnemonic,
        seedKid: prepared.seedKid,
        sigKid: prepared.sigKid,
        encKid: prepared.encKid,
        aesKid: prepared.aesKid,
    }
}

// ---- Passkey recovery devices (Release 1)

export interface PreparedRecoveryDeviceOpResult {
    success: boolean
    error?: string
    seedHex?: string
    seedKid?: string
    sigKid?: string
    encKid?: string
    aesKid?: string
}

// The key material of a passkey recovery device: a paper key whose words
// never exist. Local only.
export async function prepareRecoveryDevice(): Promise<PreparedRecoveryDeviceOpResult> {
    try {
        const wasm = await loadPracticemdWasm()
        const r = await wasm.prepareRecoveryDevice()
        return { success: true, seedHex: r.seedHex, seedKid: r.seedKid, sigKid: r.sigKid, encKid: r.encKid, aesKid: r.aesKid }
    } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) }
    }
}

// The recovery envelope for a prepared recovery device: the seed wrapped
// under the key the wasm derives from the passkey's PRF output. The PRF
// output is passed as hex and is the caller's to zero; the derived key
// never leaves the wasm call.
export async function wrapRecoverySeedForPasskey(seedHex: string, prfOutputHex: string): Promise<string> {
    const wasm = await loadPracticemdWasm()
    const keyHex = await wasm.deriveRecoveryKey(prfOutputHex)
    return wasm.wrapRecoverySeed(seedHex, keyHex)
}

// The other direction, on a browser restoring an account: the envelope the
// server answered, opened with the key derived from the assertion's PRF
// output. Returns the recovery device's seed (hex) — the authorizer seed
// provisionWithAuthorizer takes.
export async function unwrapRecoverySeedWithPasskey(envelopeB64: string, prfOutputHex: string): Promise<string> {
    const wasm = await loadPracticemdWasm()
    const keyHex = await wasm.deriveRecoveryKey(prfOutputHex)
    return wasm.unwrapRecoverySeed(envelopeB64, keyHex)
}

export interface RegisterPasskeyDeviceOpResult {
    success: boolean
    error?: string
}

// Add a passkey recovery device to an EXISTING account (the in-thread
// ask for a guest; Settings later), the way registerPaperKey adds a paper
// key. The keyring may grow during the PUK hand-off.
export async function registerPasskeyDevice(args: {
    prepared: PreparedRecoveryDeviceOpResult
    name?: string
    credentialId: string
    publicKey: string
    envelope: string
}): Promise<RegisterPasskeyDeviceOpResult> {
    if (!args.prepared.seedHex) return { success: false, error: 'registerPasskeyDevice: prepared.seedHex missing' }
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.registerPasskeyDevice(
        endpoint(), seedHex, keyringB64, args.prepared.seedHex,
        args.name || '', args.credentialId, args.publicKey, args.envelope,
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) return { success: false, error: r.error || 'registerPasskeyDevice failed' }
    return { success: true }
}

// PUBLIC (no device needed): the recovery envelope of the passkey device
// this assertion proves possession of, or found=false for every failure.
export async function recoveryEnvelope(assertion: {
    credentialId: string
    clientDataJSON: string
    authenticatorData: string
    signature: string
}): Promise<{ found: boolean; envelope?: string }> {
    const wasm = await loadPracticemdWasm()
    const r = await wasm.recoveryEnvelope(
        endpoint(), assertion.credentialId, assertion.clientDataJSON, assertion.authenticatorData, assertion.signature,
    )
    return r.found ? { found: true, envelope: r.envelope } : { found: false }
}

// One-shot prepare + register. Use ONLY in contexts where a blocking
// "save your mnemonic" prompt isn't possible (tests, scripts).
// Interactive UX (authorize flow, settings pages) must call
// preparePaperKey + registerPaperKey separately so the mnemonic is
// written down before the server commits anything.
export async function generatePaperKey(
    paperKeyName?: string,
): Promise<ProvisionPaperKeyOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.generatePaperKey(
        endpoint(),
        seedHex,
        keyringB64,
        paperKeyName || '',
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    if (!r.success) {
        return { success: false, error: r.error || 'generatePaperKey failed' }
    }
    return {
        success: true,
        mnemonic: r.mnemonic,
        seedKid: r.seedKid,
        sigKid: r.sigKid,
        encKid: r.encKid,
        aesKid: r.aesKid,
    }
}

export interface UpdateUserInfoOpResult {
    success: boolean
    error?: string
    emailUnavailable?: boolean
    emailChallenge?: boolean
    userinfo?: {
        id: string
        email: string
        email_verified: boolean
        name: string
        given_name: string
        family_name: string
    }
}

// Update the authenticated user's profile. Partial: omit any of
// givenName / familyName / email to leave that field unchanged. Empty
// string on a name field is an explicit clear. emailUnavailable=true
// means the requested email is already on another account;
// emailChallenge=true means the server accepted the email change
// pending verification. The returned userinfo keys match the legacy
// helper's snake_case shape so existing consumers don't reshape.
export async function updateUserinfo(
    updates: { givenName?: string; familyName?: string; email?: string }
): Promise<UpdateUserInfoOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const updatesJSON = JSON.stringify({
        // Only include fields the caller supplied so the server's
        // partial-update semantics kick in.
        ...(updates.givenName !== undefined ? { givenName: updates.givenName } : {}),
        ...(updates.familyName !== undefined ? { familyName: updates.familyName } : {}),
        ...(updates.email !== undefined ? { email: updates.email } : {}),
    })
    const r = await wasm.updateUserInfo(endpoint(), seedHex, updatesJSON)
    const result: UpdateUserInfoOpResult = {
        success: r.success,
        error: r.error || undefined,
        emailUnavailable: r.emailUnavailable,
        emailChallenge: r.emailChallenge,
    }
    if (r.userinfo) {
        result.userinfo = {
            id: r.userinfo.id,
            email: r.userinfo.email,
            email_verified: r.userinfo.emailVerified,
            name: r.userinfo.name,
            given_name: r.userinfo.givenName,
            family_name: r.userinfo.familyName,
        }
    }
    return result
}

export type RecipientState = 'active' | 'archived' | 'deleted' | 'accepted' | 'declined'

// Update the caller's recipient_data row for a send. For authenticated
// recipients pass ephemeralIdentityId=''; for ephemeral grantees (who
// have no direct grant) pass the sha256 of the fragment-derived seed.
export async function updateRecipientState(
    sendId: string,
    state: RecipientState,
    ephemeralIdentityId?: string
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.updateRecipientState(
        endpoint(),
        seedHex,
        sendId,
        state,
        ephemeralIdentityId || ''
    )
    return { success: r.success, error: r.error || undefined }
}

export interface ClaimSendAccessOpResult {
    success: boolean
    error?: string
    grantId?: string
}

// Convert ephemeral URL-based access into a permanent account-bound
// access_grant for the caller. Requires a signed-in user — caller
// must have a provisioned device + keyring. Matches the CLI's `sends
// claim --fragment <seed> <sendId>` flow.
//
// After this succeeds, subsequent device.readSend(sendId) calls work
// for this user WITHOUT the fragment — from any of their devices
// after a keyring pull — because the server now stores a direct
// access_grant bound to the user's identity.
export async function claimSendAccess(
    sendId: string,
    fragment: string
): Promise<ClaimSendAccessOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const result = await wasm.claimSendAccess(endpoint(), seedHex, keyringB64, sendId, fragment)
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    if (!result.success) {
        return { success: false, error: result.error || 'claimSendAccess failed' }
    }
    return { success: true, grantId: result.grantId }
}

// Request a 6-digit email verification code for an email_verified
// grant. Signed by the visitor's session throwaway so the subsequent
// verifyEphemeralAccess + retry read are all attributable to the
// same caller from the server's POV.
export async function requestEphemeralEmailVerification(
    sendId: string,
    ephemeralIdentityId: string
): Promise<{ success: boolean; error?: string }> {
    const wasm = await loadPracticemdWasm()
    const r = await wasm.requestEphemeralEmailVerification(
        endpoint(),
        getAnonymousSeedHex(),
        sendId,
        ephemeralIdentityId
    )
    return { success: r.success, error: r.error || undefined }
}

// Submit a 6-digit code to prove email control for an email_verified
// grant. On success the server stores 24h verified-access state
// keyed to the visitor's session throwaway sigKid; a subsequent
// readSendAnonymous with the same session seed then returns the
// decrypted payload.
export async function verifyEphemeralAccess(
    sendId: string,
    ephemeralIdentityId: string,
    code: string
): Promise<{ success: boolean; error?: string }> {
    const wasm = await loadPracticemdWasm()
    const r = await wasm.verifyEphemeralAccess(
        endpoint(),
        getAnonymousSeedHex(),
        sendId,
        ephemeralIdentityId,
        code
    )
    return { success: r.success, error: r.error || undefined }
}

// Dispatch the share-link notification email to an ephemeral
// recipient. Signed with the caller's real device seed (the sender
// is always logged in). Routes through device.SendEphemeralAccessEmail
// → requestSendEmailCredentials (STS) → SES SendTemplatedEmail in one
// signed flight. In dev, real SES is suppressed by default.
export interface SendEphemeralAccessEmailOpArgs {
    sendId: string
    recipientEmail: string
    accessUrl: string
    senderName?: string
    senderOrganization?: string
    recipientName?: string
    message?: string
    expiresAt?: string
}

export async function sendEphemeralAccessEmail(
    args: SendEphemeralAccessEmailOpArgs
): Promise<{ success: boolean; error?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    // Go's SendEphemeralAccessEmailArgs uses PascalCase field names
    // (no JSON tags); match that shape here.
    const argsJSON = JSON.stringify({
        SendId: args.sendId,
        RecipientEmail: args.recipientEmail,
        AccessUrl: args.accessUrl,
        SenderName: args.senderName || '',
        SenderOrganization: args.senderOrganization || '',
        RecipientName: args.recipientName || '',
        Message: args.message || '',
        ExpiresAt: args.expiresAt || '',
    })
    const r = await wasm.sendEphemeralAccessEmail(endpoint(), seedHex, argsJSON)
    return { success: r.success, error: r.error || undefined }
}

// E2E send read. ephemeralFragment is the base64url URL fragment for
// ephemeral recipients; pass undefined for authenticated recipient
// access.
export async function readSend(
    sendId: string,
    ephemeralFragment?: string,
    // Optional recipient identity hint (e.g. the team an inquiry was routed to) so a
    // never-synced team recovers via one targeted chain fetch instead of a bulk pull.
    recoveryHintIdentityId?: string
): Promise<ReadSendOpResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const result = await wasm.readSend(endpoint(), seedHex, keyringB64, sendId, ephemeralFragment || '', recoveryHintIdentityId || '')
    if (result.keyringB64 && result.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, result.keyringB64)
    }
    if (!result.success) {
        return {
            success: false,
            error: result.error || 'readSend failed',
            emailVerificationRequired: result.emailVerificationRequired,
        }
    }
    return {
        success: true,
        id: result.id,
        itemType: result.itemType,
        createdAt: result.createdAt,
        metadata: result.metadata || undefined,
        data: base64ToUint8Array(result.dataB64),
    }
}

export interface ListSendsArgs {
    recipientId?: string
    senderId?: string
    itemType?: string
    notItemType?: string
    pageSize?: number
    cursor?: string
    includeDeleted?: boolean
}

// One side of a send, resolved server-side so a listing needs no per-row
// getIdentity. displayName is '' when unknown; render displayName || identityId.
export interface SendParty {
    identityId: string
    identityType: string // 'user' | 'team'
    displayName: string
}

export interface SendSummary {
    id: string
    itemType: string
    senderSigKid: string
    recipientEncKid: string
    metadata: Record<string, any> | null
    createdAt: string
    recipientData: { status: string; statusChangedAt: string } | null
    sender: SendParty | null
    recipient: SendParty | null
    recipients: SendParty[] | null
}

export interface ListSendsOpResult {
    items: SendSummary[]
    nextCursor: string
    totalCount: number
}

// List sends filtered by recipientId (inbox) or senderId (outbox).
// Metadata-only; no payload crypto.
export async function listSends(args: ListSendsArgs): Promise<ListSendsOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const normalized: Record<string, unknown> = {
        recipientId: args.recipientId || '',
        senderId: args.senderId || '',
        itemType: args.itemType || '',
        notItemType: args.notItemType || '',
        pageSize: args.pageSize || 0,
        cursor: args.cursor || '',
        includeDeleted: !!args.includeDeleted,
    }
    // hasCard / hasAgreement use *bool on the Go side — only include
    // them when the caller explicitly set a value so "unset" stays
    // distinct from "filter to false".
    if (args.hasCard !== undefined) normalized.hasCard = args.hasCard
    if (args.hasAgreement !== undefined) normalized.hasAgreement = args.hasAgreement
    const result = await wasm.listSends(endpoint(), seedHex, JSON.stringify(normalized))
    return result
}

// --- User info ----------------------------------------------------------

export interface UserInfoOpResult {
    id: string
    name?: string
    given_name?: string
    family_name?: string
    email?: string
    email_verified?: boolean
    phone?: string
    nickname?: string
    seedKid?: string
    sigKid?: string
    encKid?: string
    aesKid?: string
    /** Release 1 (R3): membership; false = a patient guest. */
    hasRecoveryDevice?: boolean
    recoveryDueAt?: string
}

// Fetches the caller's profile + public-key material via a signed
// getUserinfo query. Always hits the server fresh — not a cache read.
export async function getUserInfo(): Promise<UserInfoOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.getUserInfo(endpoint(), seedHex)
}

// --- Admin ---------------------------------------------------------------

export async function getAdminStatus(): Promise<{ isAdmin: boolean }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.getAdminStatus(endpoint(), seedHex)
}

// --- Provider profiles (booking marketplace, §5.18/§16) ------------------

export async function listManagedProviderProfiles(
    identityId: string
): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listManagedProviderProfiles(endpoint(), seedHex, identityId)
    return r.items
}

// Admin-only global authoring view (§5.18): every profile across all identities.
export async function listAllProviderProfiles(): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listAllProviderProfiles(endpoint(), seedHex)
    return r.items
}

// --- Vetted provider-profile proposals ------------------------------------
//
// The provider-facing half of profile authoring: assigned managers list their
// profiles and submit FULL-object change requests; nothing lands on a live
// profile until a Practice MD admin vouches it per-field. Thin pass-throughs —
// every gate and rule lives server-side; the CLI proved these ops first.

export interface ProfileChangeRequestRecord {
    id: string
    profileId?: string // absent = a NEW-profile proposal (until vouch stamps it)
    recipientIdentityId: string
    recipientKind: string
    proposedDisplay: Record<string, any>
    proposedSlug?: string
    baseDisplay?: Record<string, any>
    baseSlug?: string
    status: 'submitted' | 'approved' | 'rejected' | 'superseded'
    revision: number
    parentRequestId?: string
    acceptedFields?: Record<string, any>
    decisionMessage?: string
    reviewedAt?: string
    submittedBy: string
    createdAt: string
}

export interface MyManagedProfilesResult {
    profiles: Array<Record<string, any>>
    identities: Array<{ identityId: string; kind: 'user' | 'team' }>
}

// Nav gate: does any profile route to an identity this user MANAGES (their own,
// or a team where they hold manage_provider_profiles)? Narrower than
// hasAppointmentRecipientAccess — bare team membership does not count.
export async function hasProviderProfileManageAccess(): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.hasProviderProfileManageAccess(endpoint(), seedHex)
    return !!r.hasAccess
}

// Nav gate: would the signed-in user's referral page (practicemd-api
// docs/Referrals-Architecture.md §9.2 G) have a section? The page's own rule,
// answered without building the page.
export async function hasReferralPage(): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.hasReferralPage(endpoint(), seedHex)
    return !!r.hasAccess
}

export async function listMyManagedProviderProfiles(): Promise<MyManagedProfilesResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listMyManagedProviderProfiles(endpoint(), seedHex)
    return { profiles: r.profiles || [], identities: r.identities || [] }
}

export async function listMyProviderProfileChanges(limit = 0): Promise<ProfileChangeRequestRecord[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listMyProviderProfileChanges(endpoint(), seedHex, limit)
    return (r.requests || []) as ProfileChangeRequestRecord[]
}

// Submit a proposal: the FULL display object exactly as the editor saved it (a
// key left out is a proposed DELETION). profileId set = an edit (a pending
// proposal on the profile is superseded — its author is told; the replaced id
// comes back in `superseded`); profileId '' + identityId = a NEW profile,
// created only at vouch.
export async function submitProviderProfileChange(args: {
    profileId?: string
    identityId?: string
    slug?: string
    display: Record<string, any>
}): Promise<{ request: ProfileChangeRequestRecord; superseded?: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.submitProviderProfileChange(
        endpoint(),
        seedHex,
        args.profileId || '',
        args.identityId || '',
        args.slug || '',
        JSON.stringify(args.display)
    )
    return { request: r.request as ProfileChangeRequestRecord, superseded: r.superseded || undefined }
}

// Answer a send-back: resubmit revised, chained to the rejected request.
export async function resubmitProviderProfileChange(
    requestId: string,
    display: Record<string, any>,
    slug = ''
): Promise<ProfileChangeRequestRecord> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.resubmitProviderProfileChange(endpoint(), seedHex, requestId, slug, JSON.stringify(display))
    return r.request as ProfileChangeRequestRecord
}

// The Practice MD review queue (admin), oldest first. status '' = every status.
export async function adminListProviderProfileChanges(
    status = 'submitted',
    limit = 0,
    offset = 0
): Promise<ProfileChangeRequestRecord[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminListProviderProfileChanges(endpoint(), seedHex, status, limit, offset)
    return (r.requests || []) as ProfileChangeRequestRecord[]
}

export interface ProfileChangeReview {
    // Per-(dotted-)key verdict: {"headline": "theirs", "address.city": "ours"}.
    // Omitted keys default to "theirs" — the drawer pre-selects the proposal.
    accepted?: Record<string, 'theirs' | 'ours'>
    // Keys whose proposed free text the reviewer has read (prose keys + any
    // string over 140 chars).
    acknowledged?: string[]
    // The reviewer re-reviewed the moved rows against TODAY'S live values.
    acknowledgeStale?: boolean
    note?: string
}

// The vouch. A staleness refusal RESOLVES with staleKeys set (no request/
// profile) — re-render those rows against today's values and retry with
// acknowledgeStale; every other failure rejects.
export async function adminApproveProviderProfileChange(
    requestId: string,
    review: ProfileChangeReview = {}
): Promise<{
    request?: ProfileChangeRequestRecord
    profile?: Record<string, any>
    staleKeys?: string[]
}> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminApproveProviderProfileChange(endpoint(), seedHex, requestId, JSON.stringify(review))
    return {
        request: (r.request as ProfileChangeRequestRecord) || undefined,
        profile: (r.profile as Record<string, any>) || undefined,
        staleKeys: (r.staleKeys as string[]) || undefined,
    }
}

// Send back — "needs another pass". The message is REQUIRED: it is the only
// channel the outcome travels down, read by the author verbatim.
export async function adminRejectProviderProfileChange(
    requestId: string,
    message: string,
    note = ''
): Promise<ProfileChangeRequestRecord> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminRejectProviderProfileChange(endpoint(), seedHex, requestId, message, note)
    return r.request as ProfileChangeRequestRecord
}

// Admin: move a profile to a new recipient identity (kind re-derived
// server-side; audited; the receiving identity is told). The previous
// assignee's access ends here.
export async function reassignProviderProfileRecipient(
    profileId: string,
    identityId: string
): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.reassignProviderProfileRecipient(endpoint(), seedHex, profileId, identityId)
}

// --- Demand brokerage console (admin) ---------------------------------------
//
// docs/Demand-Brokerage-Architecture.md. Thin pass-throughs by design: the
// filtering, the allow-list, and every policy refusal live in the shared Go
// device library, so this layer adds no judgement of its own. The CLI reached
// these first through httpbackend and sees the identical wire shape.

export interface OpenDemandFilter {
    specialty?: string
    zip?: string
    // Omit for "either". Sending false would silently hide every pediatric row.
    isMinor?: boolean
    unmatchedOnly?: boolean
    includeStale?: boolean
    includeSent?: boolean
    includeExpired?: boolean
    limit?: number
    offset?: number
}

// Brokerable demand for the staff console. Rows carry no patient identity — the
// server's projection has no such field to return.
export async function adminListOpenDemand(
    filter: OpenDemandFilter = {}
): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminListOpenDemand(endpoint(), seedHex, JSON.stringify(filter))
    return r.items
}

// The onboarding follow-up queue: clinicians who asked to join through the
// public /start form, newest first. Business information only — the form
// collects a practice, a contact and a provider list and says so on the page.
export async function adminListOnboardingRequests(
    limit = 0
): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminListOnboardingRequests(endpoint(), seedHex, limit)
    return r.items
}

// Recent accounts, newest first: who signed up. Staff admins only (admin grant
// AND staff-team member), enforced by the server; anyone else gets an error.
export async function adminListRecentAccounts(
    limit = 0
): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminListRecentAccounts(endpoint(), seedHex, limit)
    return r.items
}

// One submission's whole document, pretty-printed with long strings truncated
// SERVER side — which is what keeps a base64 headshot from reaching the browser
// at all, rather than reaching it and being hidden.
export async function adminGetOnboardingRequest(id: string): Promise<string> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminGetOnboardingRequest(endpoint(), seedHex, id)
    return r.summary
}

// --- Web Push (practicemd-api docs/Growth-Event-Alerting.md §6) ------------
//
// A subscription is registered against THIS device, server-side, from the
// request signature — there is no device id to pass.

export interface PushSubscriptionArgs {
    platform: 'webpush'
    token: string // the endpoint URL
    p256dh: string // base64url
    auth: string // base64url
}

export async function registerPushToken(sub: PushSubscriptionArgs): Promise<void> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    await wasm.registerPushToken(endpoint(), seedHex, JSON.stringify(sub))
}

export async function revokePushToken(token: string): Promise<void> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    await wasm.revokePushToken(endpoint(), seedHex, token)
}

export async function listMyPushTokens(): Promise<Array<{ platform: string; token: string; registeredAt: string }>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listMyPushTokens(endpoint(), seedHex)
    return r.items
}

// The key a browser must subscribe against, from the server that will do the
// sending — never from a build-time variable, which a pinned build could carry
// into the wrong environment. "" means this server does not do Web Push.
export async function pushPublicKey(): Promise<string> {
    const wasm = await loadPracticemdWasm()
    const r = await wasm.pushPublicKey(endpoint())
    return r.publicKey
}

// Records that a person followed a request up (O11), which also ends its
// notification on every staff device. Resolves to when it was FIRST recorded:
// a second call changes nothing.
export async function adminMarkOnboardingFollowedUp(id: string): Promise<string> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminMarkOnboardingFollowedUp(endpoint(), seedHex, id)
    return r.followedUpAt
}

// One search's demand plus its outreach and interest history. null when the
// search never reached a match, so there is nothing to broker.
export async function adminGetDemandDetail(
    searchId: string
): Promise<Record<string, any> | null> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminGetDemandDetail(endpoint(), seedHex, searchId)
    return r.detail
}

// The providers this patient was ACTUALLY shown, as recorded when their matches
// ran (fresh set + explore-more waves). Not a re-run — empty for demand whose
// matches predate recording.
export async function adminDemandMatches(searchId: string): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminDemandMatches(endpoint(), seedHex, searchId)
    return (r.matches || []) as Array<Record<string, any>>
}

// Mint a scoped outreach link. The returned `url` holds the ONLY copy of the
// secret that will ever exist — the server stored just its hash — so show it once
// and never expect to re-read it.
// Publishes or unpublishes a provider profile — the one lever that decides whether
// patients can discover it (matching and the public /book/<slug> lookup both gate on
// it). Deliberately NOT part of the profile save: publication is a staff decision
// about visibility, not an attribute of the content, and a content edit must never
// change it by accident.
export async function setProviderProfileListed(profileId: string, listed: boolean): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const res = await wasm.setProviderProfileListed(endpoint(), seedHex, profileId, listed)
    return !!res?.listed
}

export async function adminMintDemandOutreachToken(
    searchId: string,
    label: string,
    ttlHours: number,
    // Prefill the link from an existing provider profile: its display is snapshotted
    // onto the token, and the practice that opens it edits that snapshot. Empty mints
    // a bare link the practice fills in from scratch.
    profileId = "",
    // Aim the link at an account we already have, so the invitation appears on that
    // identity's Appointment Requests board and not only in a URL staff must send.
    // Empty is cold outreach — and the server says which happened, in `delivery`,
    // because staff act differently on each.
    recipientIdentityId = ""
): Promise<{
    success: boolean
    error?: string
    tokenId?: string
    url?: string
    expiresAt?: string
    delivery?: string
}> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.adminMintDemandOutreachToken(
        endpoint(), seedHex, searchId, label, ttlHours, profileId, recipientIdentityId)
}

// --- The review contract ----------------------------------------------------
//
// Vouching is the one moment stranger-supplied text becomes directory content a
// patient will read, so this layer sends CHOICES, never content: `accepted` maps
// a display key to "theirs" or "ours", and the server assembles the result from
// the two values it already holds. Nothing typed in a console can reach the
// directory if neither the practice submitted it nor staff already had it.

export interface InterestListFilter {
    status?: string
    searchId?: string
    limit?: number
    offset?: number
}

// The staff review queue, oldest first within a status.
export async function adminListInterestSubmissions(
    filter: InterestListFilter = {}
): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminListInterestSubmissions(endpoint(), seedHex, JSON.stringify(filter))
    return r.items
}

export interface InterestReviewArgs {
    interestId: string
    /** Display key → "theirs" | "ours". An omitted key defaults to "theirs",
     *  matching the drawer, where every row starts on the submission's side. */
    accepted?: Record<string, string>
    /** Display keys whose free text the reviewer has read. REQUIRED for any prose
     *  field taken from the submission — the server refuses the vouch otherwise,
     *  so this is not a client-side courtesy. */
    acknowledged?: string[]
    /** "update" (default) writes onto profileId; "create" authors a new profile
     *  owned by identityId — the practice that had none at mint. */
    bind?: "update" | "create"
    profileId?: string
    identityId?: string
    slug?: string
    note?: string
}

// Vouch for a practice. A refusal — unread prose, a missing profile, a submission
// somebody else already decided — arrives as success=false with the server's own
// reason, because those reasons name the next step.
export async function adminApproveInterestSubmission(
    args: InterestReviewArgs
): Promise<{ success: boolean; error?: string; recommendationsQueued?: number }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.adminApproveInterestSubmission(endpoint(), seedHex, JSON.stringify(args))
}

// Do not take a submission forward. `message` is what the practice reads on their
// own board, verbatim, and no email carries it — an empty one leaves a practice
// that expressed interest knowing nothing.
export async function adminRejectInterestSubmission(
    interestId: string,
    reason: string,
    message: string
): Promise<void> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    await wasm.adminRejectInterestSubmission(endpoint(), seedHex, interestId, reason, message)
}

// Ask ONE patient whether they are still looking — the staff console's control
// for a CHECK IN row, as distinct from the sweep that eventually asks the whole
// stale cohort. `alreadyAsked` arrives as a SUCCESS: the question a staff member
// is really asking is "has this patient been asked?", and yes is an answer.
export async function adminAskStillLooking(
    searchId: string
): Promise<{ success: boolean; error?: string; asked?: boolean; alreadyAsked?: boolean }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.adminAskStillLooking(endpoint(), seedHex, searchId)
}

// Queue providers for a patient without an interest submission — the
// staff-initiated origin, and the repair path when a vouch's own queue write
// failed.
export async function recommendProvidersToSearch(
    searchId: string,
    profileIds: string[],
    origin = ""
): Promise<{ success: boolean; error?: string; queued?: number }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.recommendProvidersToSearch(endpoint(), seedHex, searchId, profileIds.join(","), origin)
}

// --- The practice's own board -----------------------------------------------

/** One demand row as the PRACTICE sees it. De-identified by the same rule as the
 *  staff console: `status` answers whose move it is, not what happened last. */
export interface DemandBoardItem {
    key: string
    ref?: string
    status:
        | "invited"
        | "awaiting"
        | "presented"
        | "not_taken"
        | "converted"
        | "passed"
        | "booked_elsewhere"
        | "withdrawn_revised"
        | "withdrawn"
        | "surfaced"
    tokenId?: string
    interestId?: string
    // The profile this item concerns — the practice's OWN display identity
    // ("Rachel Donovan, LCSW"); absent before review binds one.
    profileName?: string
    // The receiving identity that owns this item: bound profile's owner →
    // invitation's target → submitter.
    attributedIdentityId?: string
    revision?: number
    expiresAt?: string
    createdAt?: string
    updatedAt?: string
    specialty?: string
    zip?: string
    modality?: string
    windowPref?: string
    urgency?: string
    insurance?: string
    isMinor?: boolean
    timeOfDay?: string[]
    /** The payer / vocabulary disclosure (provider-matching spec): ids from the
     *  public reference data plus the SERVER-resolved labels. Render the
     *  labels; never a raw id. */
    payerId?: string
    payerName?: string
    funding?: string
    services?: string[]
    serviceLabels?: string[]
    conditions?: string[]
    conditionLabels?: string[]
    /** What this practice wrote to us. Their own words, shown back to them. */
    note?: string
    /** The staff message on a submission not taken forward. Shown verbatim — it
     *  IS the reason, and nothing else carries it. */
    decisionMessage?: string
    profileId?: string
    /** On an interest submission: the profile content THIS practice submitted, returned so
     *  the revise surface can prefill the form that authored it. */
    draftDisplay?: Record<string, any>
}

export async function demandItemsForIdentity(identityId = ""): Promise<DemandBoardItem[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.demandItemsForIdentity(endpoint(), seedHex, identityId)
    // The wire shape is checked by the server's schema, not by this cast: the
    // status vocabulary lives in one place (api_function_demand_provider.go) and
    // this union restates it for callers.
    return r.items as DemandBoardItem[]
}

// Answer an invitation from the board — no link secret, no trip to the page.
// accept=false is "not this patient", which closes the link at once and frees
// staff to offer this patient elsewhere rather than waiting out the expiry.
export async function respondToDemandInvitation(
    tokenId: string,
    accept: boolean,
    note = ""
): Promise<{ ok: boolean; error?: string; interestId?: string; alreadySubmitted?: boolean }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.respondToDemandInvitation(endpoint(), seedHex, tokenId, accept, note)
}

// The revise loop. Rides the practice's account, never the original token, so
// acting on a staff message still works after that link has expired.
export async function resubmitDemandInterest(
    interestId: string,
    payload?: Record<string, any>,
    draftDisplay?: Record<string, any>
): Promise<{ ok: boolean; error?: string; interestId?: string; revision?: number }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.resubmitDemandInterest(
        endpoint(), seedHex, interestId,
        payload ? JSON.stringify(payload) : "",
        draftDisplay ? JSON.stringify(draftDisplay) : "")
}

// --- The tokened outreach page, once its visitor has an account --------------
//
// A stranger reading a link loads none of this: /interest fetches the anonymous
// open with a plain POST, which is what keeps an acquisition page free of a
// device layer it has no use for. These two are the other half — after the
// email-first flow has run and a device exists.

// The authenticated return trip to a link. Same operation the anonymous page
// calls, signed — which is what claims the draft parked before the account
// existed.
export async function openDemandOutreachLinkAsSelf(
    tokenId: string,
    secret: string
): Promise<{
    ok: boolean
    error?: string
    authed?: boolean
    /** This link was aimed at the caller's own account, so the invitation is
     *  already on their board — the page sends them there rather than asking
     *  somebody already signed in to sign in. */
    onYourBoard?: boolean
    /** "booked" | "withdrawn" alongside the patient-done refusal. */
    gone?: string
    parkedDraft?: { payload?: Record<string, any>; draftDisplay?: Record<string, any> }
    view?: Record<string, any>
}> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.openDemandOutreachLinkAsSelf(endpoint(), seedHex, tokenId, secret)
}

// The interest submission. `needsAuth` on a refusal means it failed ONLY for want of a
// session, so the page should run the auth flow and retry rather than showing an
// error.
export async function submitDemandInterest(
    tokenId: string,
    secret: string,
    payload: Record<string, any>,
    draftDisplay?: Record<string, any>
): Promise<{
    ok: boolean
    error?: string
    interestId?: string
    alreadySubmitted?: boolean
    needsAuth?: boolean
    /** "booked" | "withdrawn" alongside the patient-done refusal. */
    gone?: string
}> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.submitDemandInterest(
        endpoint(), seedHex, tokenId, secret,
        JSON.stringify(payload), draftDisplay ? JSON.stringify(draftDisplay) : "")
}

// Revoke a link. Nothing needs rotating: an interest submission carries no key
// material, so no secret was ever disclosed to the recipient.
export async function adminRevokeDemandOutreachToken(tokenId: string): Promise<void> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    await wasm.adminRevokeDemandOutreachToken(endpoint(), seedHex, tokenId)
}

// Public directory lookup by slug (§5.17) — unsigned, no device seed (like
// getIdentity). Returns the profile (incl. insurers) or null if the slug is unknown.
export async function getProviderProfile(slug: string): Promise<Record<string, any> | null> {
    const wasm = await loadPracticemdWasm()
    return wasm.getProviderProfile(endpoint(), slug)
}

// Practice profiles (§5.17.1). resolveBookLink is public (unsigned, no seed):
// what does /book/<slug> name — a provider, a practice, or nothing.
export async function resolveBookLink(slug: string): Promise<BookLink> {
    const wasm = await loadPracticemdWasm()
    return wasm.resolveBookLink(endpoint(), slug)
}

export async function listPracticeProfiles(): Promise<PracticeProfile[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.listPracticeProfiles(endpoint(), seedHex)
}

export async function createPracticeProfile(slug: string, display: Record<string, any>): Promise<PracticeProfile> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.createPracticeProfile(endpoint(), seedHex, slug, JSON.stringify(display))
}

export async function updatePracticeProfile(practiceId: string, slug: string, display: Record<string, any>): Promise<PracticeProfile> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.updatePracticeProfile(endpoint(), seedHex, practiceId, slug, JSON.stringify(display))
}

export async function deletePracticeProfile(practiceId: string): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.deletePracticeProfile(endpoint(), seedHex, practiceId)
}

// practiceId "" makes the profile solo. Never pass undefined across the
// wasm boundary (it would arrive as the string "<undefined>").
export async function setProviderProfilePractice(profileId: string, practiceId: string): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.setProviderProfilePractice(endpoint(), seedHex, profileId, practiceId ?? '')
}

// The practice's member order (practicemd-api docs/Booking-Architecture.md
// §5.17.1): every member, listed or not, first to last. Resolves to the
// members in their new order; a list that is not exactly the members is
// refused.
export async function arrangePracticeMembers(practiceId: string, profileIds: string[]): Promise<Record<string, any>[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.arrangePracticeMembers(endpoint(), seedHex, practiceId, profileIds)
}

// Public reference data (unsigned, no seed): taxonomy subset, license classes,
// payer master, vocabularies — what every profile picker renders.
export async function getReferenceData(): Promise<ReferenceData> {
    const wasm = await loadPracticemdWasm()
    return wasm.getReferenceData(endpoint())
}

// Registry suggestions for an NPI. SIGNED (unlike getReferenceData above):
// it reaches an external registry on demand, so it needs an accountable
// device. Read-only — it never writes the profile.
export async function nppesLookup(
    npi: string,
    displayName: string
): Promise<NppesLookupResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.nppesLookup(endpoint(), seedHex, npi, displayName || '')
}

// The public pages of a profile that is NOT published, rendered by the
// server for staff onboarding a clinician (practicemd-api
// docs/Demand-Pages.md §8). Admin-gated server-side; renders, never
// publishes. `display` is what the editor holds — saved or not.
export async function adminRenderProviderPreview(
    profileId: string,
    display: Record<string, any>,
    page: string
): Promise<ProviderPreview> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.adminRenderProviderPreview(endpoint(), seedHex, profileId || '', JSON.stringify(display || {}), page || '')
}

// Preview links (docs/Demand-Pages.md §8.1). The mint's `url` is the only copy
// of the secret there will ever be: show it once.
export async function adminMintProviderPreviewLink(
    profileId: string,
    label: string,
    ttlDays = 0
): Promise<ProviderPreviewLinkMint> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return wasm.adminMintProviderPreviewLink(endpoint(), seedHex, profileId, label || '', ttlDays || 0)
}

export async function adminRevokeProviderPreviewLink(tokenId: string): Promise<void> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    await wasm.adminRevokeProviderPreviewLink(endpoint(), seedHex, tokenId)
}

export async function adminListProviderPreviewLinks(profileId: string): Promise<ProviderPreviewLink[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminListProviderPreviewLinks(endpoint(), seedHex, profileId)
    return r.links || []
}

export async function createProviderProfile(
    identityId: string,
    slug: string,
    display: Record<string, any>
): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.createProviderProfile(endpoint(), seedHex, identityId, slug, JSON.stringify(display))
}

export async function updateProviderProfile(
    profileId: string,
    identityId: string,
    slug: string,
    display: Record<string, any>
): Promise<Record<string, any>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.updateProviderProfile(endpoint(), seedHex, profileId, identityId, slug, JSON.stringify(display))
}

export async function deleteProviderProfile(
    profileId: string
): Promise<{ success: boolean }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.deleteProviderProfile(endpoint(), seedHex, profileId)
}

export async function uploadProviderAsset(
    contentBase64: string,
    contentType: string
): Promise<{ url: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.uploadProviderAsset(endpoint(), seedHex, contentBase64, contentType)
}

// --- Ava intake (booking marketplace, §7.1/§7.7/§16) -----------------
// Both are device-signed and pre-auth capable: prepareDeviceArgs creates +
// unlocks the device if needed (the device always has a keypair, §7.7). The
// working state crosses the wasm boundary only as ciphertext.

export async function startAvaSearch(
    targetProfileSlug?: string,
    utm?: { utmSource?: string; utmMedium?: string; utmCampaign?: string; utmContent?: string }
): Promise<AvaSearchResult> {
    const { seedHex } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    // Never pass `undefined` across the JS→wasm boundary: syscall/js's
    // Value.String() turns it into the literal "<undefined>", which the server
    // then rejects as an unknown provider slug (§5.17). Send "" for a normal
    // (non-direct-link) intake so the wasm/server treat it as "no slug".
    // The acquisition tags (attribution.ts) cross as a JSON object string,
    // "" when untagged, for the same reason.
    return (await wasm.startAvaSearch(endpoint(), seedHex, targetProfileSlug ?? '', utm ? JSON.stringify(utm) : '')) as AvaSearchResult
}

// previewSearchTurn renders the first turn WITHOUT minting a search (§5.14); the
// search is created on the first answer. Avoids a draft per /ava visit. entry
// optionally names a chart entry point (docs/Ava-Statechart.md §4.5, e.g.
// "revise") — the server resolves the name; the client never pins node ids.
export async function previewAvaSearch(entry?: string): Promise<AvaTurn> {
    const { seedHex } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.previewSearchTurn(endpoint(), seedHex, entry ?? '')
    return r.turn as AvaTurn
}

// The Appointment Request Details document (§6.3), built by the shared library
// so this client seals what the CLI seals. The rule lived here in TypeScript
// (buildDetails) with no second implementation to disagree with it — which is
// not the same as being agreed upon.
export async function buildAppointmentRequestDetails(
    state: Record<string, any>
): Promise<Record<string, any>> {
    const wasm = await loadPracticemdWasm()
    const json = wasm.buildAppointmentRequestDetails(JSON.stringify(state ?? {}))
    if (!json) throw new Error("could not build the appointment request details")
    return JSON.parse(json)
}

export async function advanceAvaTurn(
    searchId: string,
    nodeId: string,
    answerJSON: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number,
    userBubble = "",
    // The §4.11 capability declaration. Leave it undefined — the shared library
    // declares what this build can actually drive, beside the code that drives
    // it, so the page cannot under-declare by forgetting to pass it. Pass [] to
    // declare NOTHING, which is how a stale bundle is simulated in tests.
    clientEvents?: string[],
    // Client-asserted conversation-mode facts merged into the decrypted prior
    // state (e.g. the §21.7 _revising flag). Usually undefined.
    stateOverrides?: Record<string, any>
): Promise<AvaAdvanceResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.advanceAvaTurn(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        nodeId,
        answerJSON,
        JSON.stringify(selfGrant),
        JSON.stringify(stateSnapshot),
        seq,
        userBubble,
        clientEvents === undefined ? undefined : clientEvents.join(","),
        stateOverrides ? JSON.stringify(stateOverrides) : ""
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return { turn: r.turn as AvaTurn, stateSnapshot: r.stateSnapshot, nextSeq: r.nextSeq }
}


// sendInquiriesAndAdvance interprets the §4.8 send_inquiries input effect in
// one shared-library call: the encrypted fan-out FIRST, then the chart advance
// sealing the selection as the answer — so SENT never renders without real
// inquiries behind it, and the ordering cannot drift per client (the CLI
// discharges the same obligation through the same composite). On a
// sent-but-unsealed failure the whole call is the retry: the server holds one
// live request per listing (§8.1), so a re-run cannot double-send.
export async function sendInquiriesAndAdvance(
    searchId: string,
    nodeId: string,
    providers: Array<Record<string, any>>,
    revising: boolean,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number,
    stateOverrides?: Record<string, any>
): Promise<AvaAdvanceResult & { sent: number; withdrawn: number }> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.sendInquiriesAndAdvance(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        nodeId,
        JSON.stringify(providers),
        JSON.stringify(selfGrant),
        JSON.stringify(stateSnapshot),
        seq,
        revising,
        undefined, // clientEvents: the shared library declares what this build drives
        stateOverrides ? JSON.stringify(stateOverrides) : ""
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return {
        turn: r.turn as AvaTurn,
        stateSnapshot: r.stateSnapshot,
        nextSeq: r.nextSeq,
        sent: r.sent,
        withdrawn: r.withdrawn,
    }
}

// runAvaMatch runs the provider match and tells the machine what happened —
// suggest, classify, signal, persist — in one device-layer call. The
// classification (some / none / failed) deliberately lives in the shared device
// library rather than here: it is the same judgement for every client, and
// deciding it per-client is exactly how a failed lookup came to be rendered as
// an empty provider card.
export async function runAvaMatch(
    searchId: string,
    nodeId: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number
): Promise<AvaMatchResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.runAvaMatch(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        nodeId,
        JSON.stringify(selfGrant),
        JSON.stringify(stateSnapshot),
        seq
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return {
        outcome: r.outcome as AvaMatchResult["outcome"],
        providers: r.providers || [],
        turn: r.turn as AvaTurn | undefined,
        stateSnapshot: r.stateSnapshot,
        nextSeq: r.nextSeq,
    }
}

/** The outcome of draining queued provider recommendations. */
export interface AvaDrainResult {
    /** How many recommendations were rendered into the conversation. */
    drained: number
    providers: Array<Record<string, any>>
    /** interest | staff | sweep | retry — decides which copy the patient reads. */
    origin: string
    /** Recommendations WERE queued but this state does not accept them yet. Nothing
     *  was written and nothing acked, so the queue survives for the next attempt. */
    deferred: boolean
    turn?: AvaTurn
    stateSnapshot: Record<string, any>
    nextSeq: number
    /** Set when the drain succeeded but the settle did not. Not a failure — the
     *  recommendation is already sealed in the transcript; it may render again. */
    ackError?: string
}

// drainAvaRecommendations renders providers staff have queued for this patient.
//
// A CLIENT operation by necessity, not convenience: the transcript is sealed under the
// search content key, which never leaves the device, so the server cannot write a
// bubble. The server publishes; the client drains.
//
// Safe to call at any time — nothing queued is a no-op, and a conversation that cannot
// accept the event defers without writing.
// AvaPendingDrainResult is one ordered pass over everything queued for this
// patient. The ORDER and the one suppression rule between the items are policy
// written permanently into the transcript, so they live in the device library
// (DrainAvaPending) — this engine used to carry two copies of that rule and the
// CLI a third that omitted the gate.
export interface AvaPendingDrainResult {
    recommendations?: {
        drained: number
        providers: Array<Record<string, any>>
        deferred: boolean
        turn?: AvaTurn
        ackError?: string
    }
    stillLooking?: { rendered: boolean; deferred: boolean; turn?: AvaTurn }
    staffMessages?: { turns: AvaTurn[] }
    /** Release 1: the in-thread recovery ask, sealed last for a guest when
     *  something arrived or the grace ran out. */
    recoveryAsk?: { rendered: boolean; deferred: boolean; turn?: AvaTurn }
    nodeId: string
    stateSnapshot: Record<string, any>
    nextSeq: number
    // Per-drain failures. A failing drain never aborts the pass: a recommendation
    // that could not be sealed must not also cost the patient a message somebody
    // sent them.
    errors?: Record<string, string>
}

export async function drainAvaPending(
    searchId: string,
    nodeId: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number
): Promise<AvaPendingDrainResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.drainAvaPending(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        nodeId,
        JSON.stringify(selfGrant),
        JSON.stringify(stateSnapshot),
        seq
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return {
        recommendations: r.recommendations,
        stillLooking: r.stillLooking,
        staffMessages: r.staffMessages,
        recoveryAsk: r.recoveryAsk,
        nodeId: r.nodeId,
        stateSnapshot: r.stateSnapshot,
        nextSeq: r.nextSeq,
        errors: r.errors,
    }
}

export async function drainAvaRecommendations(
    searchId: string,
    nodeId: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number
): Promise<AvaDrainResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.drainAvaRecommendations(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        nodeId,
        JSON.stringify(selfGrant),
        JSON.stringify(stateSnapshot),
        seq
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return {
        drained: r.drained,
        providers: r.providers || [],
        origin: r.origin,
        deferred: r.deferred,
        turn: r.turn as AvaTurn | undefined,
        stateSnapshot: r.stateSnapshot,
        nextSeq: r.nextSeq,
        ackError: r.ackError,
    }
}

/** A pending "are you still looking?" check-in, if there is one. */
export interface StillLookingCheck {
    pending: boolean
    specialty?: string
    zip?: string
    askedAt?: string
}

/** The outcome of rendering the check-in turn. `rendered: false` with no
 *  `deferred` means there was simply nothing to ask — the common case, and not a
 *  failure. */
export interface StillLookingResult {
    rendered: boolean
    deferred: boolean
    turn?: AvaTurn
    stateSnapshot?: Record<string, any>
    nextSeq: number
}

// drainAvaStillLookingCheck renders the stale check-in into the conversation.
//
// A CLIENT operation for the same reason draining recommendations is: the
// transcript is sealed under a content key that never leaves the device, so the
// server can publish the question but cannot write the bubble.
//
// Safe to call on every Ava open — nothing pending is a no-op, and a conversation
// that cannot take the question defers without writing.
export async function drainAvaStillLookingCheck(
    searchId: string,
    nodeId: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number
): Promise<StillLookingResult> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.drainAvaStillLookingCheck(
        endpoint(), seedHex, keyringB64, searchId, nodeId,
        JSON.stringify(selfGrant), JSON.stringify(stateSnapshot), seq)
    return {
        rendered: !!r.rendered,
        deferred: !!r.deferred,
        turn: r.turn as AvaTurn | undefined,
        stateSnapshot: r.stateSnapshot,
        nextSeq: r.nextSeq,
    }
}

// --- Staff↔patient demand messaging (§8) -----------------------------------

// One thread entry. authorUserId arrives only for staff callers (audit) — the
// patient renders every staff message as "Practice MD".
export interface DemandThreadMessage {
    messageId: string
    sendId: string
    direction: "to_patient" | "to_staff"
    authorUserId?: string
    seq: number
    createdAt: number
}

export interface DemandThread {
    searchId: string
    staffTeamId: string
    patientIdentityId?: string
    patientEncKid?: string
    messages: DemandThreadMessage[]
}

export async function getDemandThread(searchId: string): Promise<DemandThread | null> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.getDemandThread(endpoint(), seedHex, searchId)
    if (!r) return null
    return {
        searchId: r.SearchId ?? r.searchId ?? searchId,
        staffTeamId: r.StaffTeamId ?? r.staffTeamId ?? "",
        patientIdentityId: r.PatientIdentityId ?? r.patientIdentityId ?? undefined,
        patientEncKid: r.PatientEncKid ?? r.patientEncKid ?? undefined,
        messages: ((r.Messages ?? r.messages ?? []) as Array<Record<string, any>>).map(m => ({
            messageId: m.MessageId ?? m.messageId,
            sendId: m.SendId ?? m.sendId,
            direction: m.Direction ?? m.direction,
            authorUserId: m.AuthorUserId ?? m.authorUserId ?? undefined,
            seq: m.Seq ?? m.seq ?? 0,
            createdAt: m.CreatedAt ?? m.createdAt ?? 0,
        })),
    }
}

// sendDemandMessage is the STAFF send (console): recipient = the patient, with
// the enc kid the thread projection supplied, self = the staff team.
export async function sendDemandMessage(args: {
    searchId: string
    recipientIdentityId: string
    recipientEncKid?: string
    selfIdentityId: string
    text: string
}): Promise<{ messageId: string; sendId: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.sendDemandMessage(
        endpoint(), seedHex, args.searchId, args.recipientIdentityId,
        args.recipientEncKid ?? "", args.selfIdentityId, noteToB64(args.text))
    return { messageId: r.messageId, sendId: r.sendId }
}

export interface StaffMessagesDrainResult {
    turns: AvaTurn[]
    stateSnapshot?: Record<string, any>
    nextSeq: number
}

// drainAvaStaffMessages seals undrained "Practice MD" messages into the sealed
// conversation as speech turns — no chart movement — and acks after persist.
// Safe on every open: nothing pending is a no-op.
export async function drainAvaStaffMessages(
    searchId: string,
    nodeId: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number
): Promise<StaffMessagesDrainResult> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.drainAvaStaffMessages(
        endpoint(), seedHex, keyringB64, searchId, nodeId,
        JSON.stringify(selfGrant), JSON.stringify(stateSnapshot), seq)
    return {
        turns: (r.turns ?? []) as AvaTurn[],
        stateSnapshot: r.stateSnapshot,
        nextSeq: r.nextSeq,
    }
}

// userBubbleForAnswer renders an answer as the patient's own words — the echo
// shown immediately AND the text sealed for replay (§5.14). It calls the device
// library's implementation rather than re-deriving the rule here: the value is
// written into a permanent artifact, so a second version in TypeScript would be
// a rule that must agree, and any drift would make a replayed conversation show
// different words than the patient saw. Synchronous — a pure string function.
export async function userBubbleForAnswer(answerJSON: string): Promise<string> {
    const wasm = await loadPracticemdWasm()
    return wasm.userBubbleForAnswer(answerJSON)
}

// shareLinkURL builds the URL a patient's Share hands a friend (practicemd-api
// docs/Referrals-Architecture.md §10.3) with the device library's builder, so
// the web and the CLI build the same string. Synchronous in the wasm; the card
// awaits this when it mounts, never inside the click, because Safari keeps the
// user gesture only for work done before the first await.
export async function shareLinkURL(slug: string, profileId: string): Promise<string> {
    const wasm = await loadPracticemdWasm()
    return wasm.shareLinkURL(window.location.origin, slug, profileId)
}

// sendPatientStaffMessage is the patient writing to Practice MD — sending
// E2EE to the staff team AND sealing the patient's own bubble. Whether the
// message opens an exchange is derived in the device library and sealed there;
// no caller supplies it.
export async function sendPatientStaffMessage(
    searchId: string,
    nodeId: string,
    text: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    seq: number
): Promise<{ messageId: string; sendId: string; turn: AvaTurn; stateSnapshot?: Record<string, any>; nextSeq: number }> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.sendPatientStaffMessage(
        endpoint(), seedHex, keyringB64, searchId, nodeId, text,
        JSON.stringify(selfGrant), JSON.stringify(stateSnapshot), seq)
    return {
        messageId: r.messageId,
        sendId: r.sendId,
        turn: r.turn as AvaTurn,
        stateSnapshot: r.stateSnapshot,
        nextSeq: r.nextSeq,
    }
}

export async function setDemandMessagesRead(searchId: string, read: boolean): Promise<number> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.setDemandMessagesRead(endpoint(), seedHex, searchId, read)
    return r?.affected ?? 0
}

export interface UnreadDemandThread {
    searchId: string
    unreadCount: number
    lastMessageAt: number
    searchPhase: string
    resolution: string
    hasDemandRow: boolean
    specialty: string
    zip: string
}

export async function adminListUnreadDemandThreads(): Promise<UnreadDemandThread[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const items = (await wasm.adminListUnreadDemandThreads(endpoint(), seedHex)) ?? []
    return (items as Array<Record<string, any>>).map(t => ({
        searchId: t.SearchId ?? t.searchId,
        unreadCount: t.UnreadCount ?? t.unreadCount ?? 0,
        lastMessageAt: t.LastMessageAt ?? t.lastMessageAt ?? 0,
        searchPhase: t.SearchPhase ?? t.searchPhase ?? "",
        resolution: t.Resolution ?? t.resolution ?? "",
        hasDemandRow: !!(t.HasDemandRow ?? t.hasDemandRow),
        specialty: t.Specialty ?? t.specialty ?? "",
        zip: t.Zip ?? t.zip ?? "",
    }))
}

export async function adminGetStaffTeam(): Promise<{ teamId: string; teamName: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminGetStaffTeam(endpoint(), seedHex)
    return { teamId: r?.teamId ?? "", teamName: r?.teamName ?? "" }
}

export async function adminDesignateStaffTeam(teamId: string): Promise<{ teamId: string; teamName: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminDesignateStaffTeam(endpoint(), seedHex, teamId)
    return { teamId: r?.teamId ?? "", teamName: r?.teamName ?? "" }
}

/** How a create or update saves a research run's accepted findings with
 *  the payer (practicemd-api docs/Payer-Master-Plan.md §7.1). */
export type PayerFromResearch = { runId: string; accept: number[] }

/** A Payers page operation's answer: the server's Dictionary. A refusal the
 *  staff member caused is data (`ok: false`, `error`, and for a master write
 *  the rules it breaks in `problems`); only a failure rejects, with a string
 *  (read it with wasmErrorMessage). */
export type PayerOpResult = Record<string, any>

async function payerOp(call: (wasm: any, endpoint: string, seedHex: string) => Promise<PayerOpResult>): Promise<PayerOpResult> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await call(wasm, endpoint(), seedHex)
}

export async function adminCreatePayer(payer: Record<string, any>, fromResearch?: PayerFromResearch): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminCreatePayer(e, s, JSON.stringify(payer), fromResearch ? JSON.stringify(fromResearch) : ''))
}

export async function adminUpdatePayer(payer: Record<string, any>, fromResearch?: PayerFromResearch): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminUpdatePayer(e, s, JSON.stringify(payer), fromResearch ? JSON.stringify(fromResearch) : ''))
}

export async function adminRetirePayer(payerId: string, into: string): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminRetirePayer(e, s, payerId, into))
}

export async function adminSetAvaPayerChips(state: string, chips: string[]): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminSetAvaPayerChips(e, s, state, JSON.stringify(chips)))
}

export async function adminImportPayers(master: Record<string, any>, overwrite: boolean): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminImportPayers(e, s, JSON.stringify(master), overwrite))
}

export async function adminPreviewPayerChange(change: Record<string, any>): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminPreviewPayerChange(e, s, JSON.stringify(change)))
}

export async function adminPayerUsedBy(payerId: string): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminPayerUsedBy(e, s, payerId))
}

export async function adminPayersByState(): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminPayersByState(e, s))
}

export async function adminPayerChangeLog(payerId: string, limit = 0): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminPayerChangeLog(e, s, payerId, limit))
}

export async function adminPayerResearchSources(states: string[]): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminPayerResearchSources(e, s, JSON.stringify(states)))
}

export async function adminResearchPayer(name: string, states: string[]): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminResearchPayer(e, s, name, JSON.stringify(states)))
}

export async function adminRecheckPayer(payerId: string): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminRecheckPayer(e, s, payerId))
}

export async function adminResearchPayerAgain(runId: string): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminResearchPayerAgain(e, s, runId))
}

export async function adminPayerResearchRuns(status = '', before = '', limit = 0): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminPayerResearchRuns(e, s, status, before, limit))
}

export async function adminPayerResearchRun(runId: string): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminPayerResearchRun(e, s, runId))
}

export async function adminDecidePayerFinding(runId: string, ordinal: number, decision: 'accepted' | 'rejected' | ''): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminDecidePayerFinding(e, s, runId, ordinal, decision))
}

export async function adminLeaveOutPayerResearch(runId: string, reason: string): Promise<PayerOpResult> {
    return payerOp((wasm, e, s) => wasm.adminLeaveOutPayerResearch(e, s, runId, reason))
}

// settleStillLookingCheck records the answer AND does what it means.
//
// Separate from the turn on purpose: a staff board un-staling, or an entire
// search being withdrawn and its outreach revoked, is the server's to do — a
// client that rendered the turn and skipped this call would leave the patient's
// answer with no effect at all. Returns whether the search was WITHDRAWN, so the
// conversation can say so plainly.
export async function settleStillLookingCheck(
    searchId: string,
    keepLooking: boolean
): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.settleStillLookingCheck(endpoint(), seedHex, searchId, keepLooking)
    return !!r?.withdrawn
}

export async function suggestAvaProviders(
    searchId: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>,
    excludeProfileIds: string[] = []
): Promise<{ providers: Array<Record<string, any>>; stateSnapshot: Record<string, any> }> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.suggestAvaProviders(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        JSON.stringify(selfGrant),
        JSON.stringify(stateSnapshot),
        JSON.stringify(excludeProfileIds)
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    // stateSnapshot now carries the accumulated candidate pool (§10.2) — the
    // caller must hold it so later turns persist on top of the pool.
    return { providers: r.items || [], stateSnapshot: r.stateSnapshot || stateSnapshot }
}

export async function bindAvaSearchToAccount(
    searchId: string,
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>
): Promise<{ selfGrant: Record<string, any>; state: Record<string, any> }> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.bindAvaSearchToAccount(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        JSON.stringify(selfGrant),
        JSON.stringify(stateSnapshot)
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return { selfGrant: r.selfGrant, state: r.state || {} }
}

// One selected provider to send an appointment request inquiry to
// (Booking-Architecture §8.1). `plaintextB64` is the base64-encoded
// "Appointment Request Details" JSON (§6.3) — built by the caller from the
// search working state.
export interface AppointmentInquiryInput {
    sourceProfileId: string
    recipientIdentityId: string
    plaintextB64: string
    metadata?: Record<string, any>
}

export interface AppointmentInquiriesResult {
    success: boolean
    requestIds: string[]
    results: Array<{ sourceProfileId: string; sendId: string; requestId: string }>
}

// One inbound inquiry on a recipient's board (§5.19) — the controlled
// projection (no search_id/origin). Clinical Details are read from sendId via
// readSend.
export interface AppointmentRequestBoardItem {
    requestId: string
    state: string
    recipientIdentityId: string
    recipientKind: string
    sourceProfileId: string
    sourceProfileName: string
    sourceProfileSlug: string
    sendId: string
    patientRating: string
    createdAt: number
    expiresAt: number
    offeredStart: number
    offeredDurationMinutes: number
    unreadCount: number
}

export interface ListAppointmentRequestsArgs {
    recipientIdentityId: string
    state?: string
    sourceProfileId?: string
    limit?: number
    offset?: number
}

// listAppointmentRequests returns the inbound inquiry board for one recipient
// identity (§5.19). Membership-gated server-side: the caller must be that user
// identity or a member of that team.
export async function listAppointmentRequests(
    args: ListAppointmentRequestsArgs
): Promise<AppointmentRequestBoardItem[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listAppointmentRequests(
        endpoint(),
        seedHex,
        args.recipientIdentityId,
        args.state || '',
        args.sourceProfileId || '',
        args.limit || 0,
        args.offset || 0
    )
    return r.items as AppointmentRequestBoardItem[]
}

// Mark a thread's inbound messages read (ack) or unread (clear acks) for the
// caller's recipient identity — human-read (§13), NOT readSend. Returns the count
// of message IBMs whose read-state changed.
export async function setAppointmentMessagesRead(requestId: string, read: boolean): Promise<number> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.setAppointmentMessagesRead(endpoint(), seedHex, requestId, read)
    return (r?.affected as number) || 0
}

// Add form templates to a request's forms-to-fill set (§11.4) — the recipient's
// OWN identity-scoped templates (the dashboard set), additive and any-time.
// Returns the merged set.
export async function requestAppointmentForms(requestId: string, templateIds: string[]): Promise<string[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.requestAppointmentForms(endpoint(), seedHex, requestId, JSON.stringify(templateIds))
    return (r?.formTemplateIds as string[]) || []
}

// Record a patient's COMPLETED form (a client form send) as a thread entry (§11.4).
// The body is an ordinary writeSend itemType="form" stamped with appointment_request_id;
// this records the reference. Server validates cleartext (no decryption). Returns the id.
export async function recordAppointmentFormSubmission(requestId: string, templateId: string, formSendId: string): Promise<string> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.recordAppointmentFormSubmission(endpoint(), seedHex, requestId, templateId, formSendId)
    return (r?.submissionId as string) || ''
}

// --- Patient surface (§5.14/§6.3): my searches + inquiries -----------------

export interface MyAppointmentSearch {
    searchId: string
    phase: string
    createdAt: number
    sentAt: number // 0 while in-progress (unsent)
    selfGrant: Record<string, any>
    stateSnapshot: Record<string, any>
    // Rail signals (cleartext, server-derived — the redesigned rail). Zero/empty
    // when not applicable (no live offers, not booked).
    liveOfferCount: number
    /** Providers staff queued that this patient has not been shown yet. */
    pendingRecommendationCount: number
    /** Practice MD messages not yet drained into the conversation (§8). */
    pendingStaffMessageCount: number
    lastActivityAt: number // unix seconds; MAX over the search + its requests/offers
    bookedProviderName: string // "" unless booked
    bookedProviderCred: string // booked provider's credentials (e.g. "LCSW"); "" if none — composed with name into the credentialed form
    bookedAt: number // unix seconds of the accepted offer's start; 0 unless booked
    outcome: string // booked request's state: booked|attended|no_show|late_cancelled|cancelled; "" if not booked (§11.6)
    rated: boolean // patient has rated the booked provider yet (§12.1) — drives the rail's rate cue
    archived: boolean // soft-archived (in the Archived view, not the active rail)
    withdrawnAt: number // unix seconds the patient withdrew; 0 if not user-withdrawn
}

export interface MyRequestInquiry {
    requestId: string
    state: string
    /** "" | "revised" — why a withdrawn inquiry was withdrawn (§8 decided B). */
    withdrawnReason?: string
    recipientIdentityId: string
    sourceProfileName: string
    sourceProfileSlug: string
    sendId: string
    createdAt: number
    /** The post-visit prompt (practicemd-api docs/Referrals-Architecture.md
     *  §10.3): the clinician the inquiry went to, whether their page is listed
     *  (Share shows only then), and the patient's own Would book again, 1-5;
     *  absent before rating and while ratings are switched off. */
    sourceProfileId?: string
    sourceProfileListed?: boolean
    wouldBookAgain?: number | null
}

// The patient's own searches — in-progress + sent (§5.14).
export async function listMyAppointmentSearches(): Promise<MyAppointmentSearch[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listMyAppointmentSearches(endpoint(), seedHex)
    return r.items as MyAppointmentSearch[]
}

// A search's inquiries — "everyone I asked" (§6.3).
export async function listMyRequestInquiries(searchId: string): Promise<MyRequestInquiry[]> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.listMyRequestInquiries(endpoint(), seedHex, searchId)
    return r.items as MyRequestInquiry[]
}

// Decrypt a search's snapshot locally (§5.16) — for the summary label.
export async function decryptSearchState(
    selfGrant: Record<string, any>,
    stateSnapshot: Record<string, any>
): Promise<Record<string, any>> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.decryptSearchState(
        seedHex,
        keyringB64,
        JSON.stringify(selfGrant || {}),
        JSON.stringify(stateSnapshot || {})
    )
    return r.state || {}
}

// --- Negotiation (§11): offer / accept / decline / thread -----------------

export interface ThreadOffer {
    offerId: string
    sendId: string
    offeredStart: string
    durationMinutes: number
    timezone: string
    state: string
    createdAt: number
}

export interface ThreadMessage {
    messageId: string
    sendId: string // the encrypted body; read via readSend
    direction: string // 'to_patient' | 'to_provider'
    authorUserId: string
    createdAt: number
}

export interface ThreadForm {
    submissionId: string
    templateId: string
    formSendId: string // the completed form's send; read via readSend
    createdAt: number
}

export interface AppointmentThread {
    requestId: string
    state: string
    inquirySendId: string
    recipientIdentityId: string
    patientIdentityId: string
    offers: ThreadOffer[]
    messages: ThreadMessage[]
    forms: ThreadForm[] // completed-form thread entries (§15.6)
    formTemplateIds: string[] // forms-to-fill the recipient asked for (§11.4)
    sourceProfileName: string // patient-facing: the provider listing's display name (§5.19)
    sourceProfileCred: string // patient-facing: the provider's credentials (e.g. LCSW)
    rated: boolean // the patient has already rated the provider for this request (§12.1)
}

export interface SendMessageInput {
    requestId: string
    recipientIdentityId: string // the OTHER endpoint (the message recipient)
    selfIdentityId: string // MY endpoint (the self-grant target)
    text: string
}

export interface MakeOfferInput {
    requestId: string
    recipientIdentityId: string
    patientIdentityId: string
    offeredStart?: string // RFC3339
    durationMinutes?: number
    timezone?: string
    note?: string // optional free-text plaintext
}

function noteToB64(note?: string): string {
    if (!note) return ''
    const bytes = new TextEncoder().encode(note)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary)
}

// makeAppointmentOffer (§11.2): the offering member's device builds the offer
// send (granted to the patient + a self-grant) and posts it with the cleartext
// scheduling.
export async function makeAppointmentOffer(
    args: MakeOfferInput
): Promise<{ offerId: string; sendId: string; requestId: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.makeAppointmentOffer(
        endpoint(),
        seedHex,
        args.requestId,
        args.recipientIdentityId,
        args.patientIdentityId,
        args.offeredStart || '',
        args.durationMinutes || 0,
        args.timezone || '',
        noteToB64(args.note)
    )
}

// sendAppointmentMessage (§11.4): either endpoint appends a free-text message to
// a thread. The device encrypts it to the OTHER endpoint + a self-grant. The body
// rides the send; read it back via readSend(sendId).
export async function sendAppointmentMessage(
    args: SendMessageInput
): Promise<{ messageId: string; sendId: string; requestId: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.sendAppointmentMessage(
        endpoint(),
        seedHex,
        args.requestId,
        args.recipientIdentityId,
        args.selfIdentityId,
        noteToB64(args.text)
    )
}

// acceptAppointmentOffer (§11.3): the patient books by accepting an offer.
export async function acceptAppointmentOffer(
    offerId: string
): Promise<{ success: boolean; requestId: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.acceptAppointmentOffer(endpoint(), seedHex, offerId)
}

// recordAppointmentOutcome (§11.6): the provider settles a booked visit's outcome.
export async function recordAppointmentOutcome(
    requestId: string,
    outcome: string
): Promise<{ success: boolean; requestId: string; state: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.recordAppointmentOutcome(endpoint(), seedHex, requestId, outcome)
}

// rateProvider (§12.1): the patient rates the clinician after an attended visit.
export async function rateProvider(
    requestId: string,
    dimensions: Record<string, number>
): Promise<{ success: boolean }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.rateProvider(endpoint(), seedHex, requestId, JSON.stringify(dimensions || {}))
}

// ratePatient (§12.2): the provider rates the patient's conduct after the outcome.
export async function ratePatient(
    requestId: string,
    score: number,
    conductTags: string[]
): Promise<{ success: boolean }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.ratePatient(endpoint(), seedHex, requestId, score, JSON.stringify(conductTags || []))
}

// getMyPatientReputation (§12.3): the patient's own de-identified conduct aggregate.
export async function getMyPatientReputation(): Promise<{ score: number; count: number }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.getMyPatientReputation(endpoint(), seedHex)
}

// ratingsEnabled (§22.2): the kill-switch state, for hiding the rating UI.
export async function ratingsEnabled(): Promise<{ enabled: boolean }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.ratingsEnabled(endpoint(), seedHex)
}

// declineAppointmentRequest (§11.2): a provider declines a pending inquiry.
export async function declineAppointmentRequest(requestId: string): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.declineAppointmentRequest(endpoint(), seedHex, requestId)
    return !!r.success
}

// declineAppointmentOffer (§11.3): the patient passes on a specific offer.
export async function declineAppointmentOffer(offerId: string): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.declineAppointmentOffer(endpoint(), seedHex, offerId)
    return !!r.success
}

// withdrawAppointmentOffer (§11.2): the provider rescinds a live offer.
export async function withdrawAppointmentOffer(offerId: string): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.withdrawAppointmentOffer(endpoint(), seedHex, offerId)
    return !!r.success
}

// deleteAppointmentSearch (§5.14): the patient discards their own search.
export async function deleteAppointmentSearch(searchId: string): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.deleteAppointmentSearch(endpoint(), seedHex, searchId)
    return !!r.success
}

// archiveAppointmentSearch / unarchiveAppointmentSearch (§5.14): soft-remove a
// search from the rail (kept for replay) and restore it (Archived view / undo).
export async function archiveAppointmentSearch(searchId: string): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.archiveAppointmentSearch(endpoint(), seedHex, searchId)
    return !!r.success
}

export async function unarchiveAppointmentSearch(searchId: string): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.unarchiveAppointmentSearch(endpoint(), seedHex, searchId)
    return !!r.success
}

// withdrawAppointmentSearch (§11.3/§11.5): soft pull-back of a sent search —
// inquiries → withdrawn (history preserved). recipientId "" = the whole search.
export async function withdrawAppointmentSearch(searchId: string, recipientId = "", reason = ""): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.withdrawAppointmentSearch(endpoint(), seedHex, searchId, recipientId, reason)
    return !!r.success
}

// One rendered turn from a search transcript, decrypted (§5.14). `turn` is the
// verbatim rendered payload: the patient's reply (userBubble), then the Ava
// bubbles (with the empathy beat already folded into bubbles[0], as seen live).
export interface TranscriptReplayEntry {
    seq: number
    role: string
    kind: string
    turn: {
        userBubble?: string
        bubbles?: string[]
        nextNodeId?: string
        input?: Record<string, any> | null
    }
}

// getAvaTranscript (§5.14, §10.2): re-open a search and return its decrypted
// intake transcript (in seq order, for a static replay) PLUS the decrypted
// working state (the candidate pool + Details), so a return visit can re-render
// the persisted providers and send to one. Keyring may grow during PUK; persisted.
export async function getAvaTranscript(searchId: string): Promise<{
    entries: TranscriptReplayEntry[]
    state: Record<string, any>
    // The server's CURRENT self-grant and sealed snapshot — the grant this read
    // just proved openable. A caller continuing the conversation must adopt
    // these; a stale rail handle answers into "no matching key in local keyring".
    selfGrant?: Record<string, any>
    stateSnapshot?: Record<string, any>
}> {
    const { seedHex, keyringB64 } = await prepareDeviceArgs({ allowSilent: true })
    const wasm = await loadPracticemdWasm()
    const r = await wasm.getAvaTranscript(endpoint(), seedHex, keyringB64, searchId)
    return {
        entries: (r.entries || []) as TranscriptReplayEntry[],
        state: r.state || {},
        selfGrant: r.selfGrant || undefined,
        stateSnapshot: r.stateSnapshot || undefined,
    }
}

// getAppointmentThread (§6.6): the head inquiry + its offers, to either endpoint.
export async function getAppointmentThread(requestId: string): Promise<AppointmentThread | null> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.getAppointmentThread(endpoint(), seedHex, requestId)
    return (r as AppointmentThread) || null
}

// hasAppointmentRecipientAccess (§5.19): nav gate — is the caller (self or a team
// they belong to) the recipient of any provider profile? Drives whether the
// appointment-requests link appears. Cleartext routing only; no decryption.
export async function hasAppointmentRecipientAccess(): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.hasAppointmentRecipientAccess(endpoint(), seedHex)
    return !!r.hasAccess
}

// hasTeamMembership: nav gate — does the caller belong to >=1 team? Drives
// whether Dashboard / Form Templates / Designer appear. Cleartext routing only.
export async function hasTeamMembership(): Promise<boolean> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.hasTeamMembership(endpoint(), seedHex)
    return !!r.hasTeamMembership
}

// sendAppointmentRequestInquiries posts the request-inquiry fan-out (§8.1):
// one encrypted single-recipient send per selected provider, all in one
// device-signed call. Requires an authenticated device (the patient's PUK is
// the self-grant recipient and the signer).
export async function sendAppointmentRequestInquiries(
    searchId: string,
    inquiries: AppointmentInquiryInput[]
): Promise<AppointmentInquiriesResult> {
    const { seedHex, keyringB64, record } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.sendAppointmentRequestInquiries(
        endpoint(),
        seedHex,
        keyringB64,
        searchId,
        JSON.stringify(inquiries)
    )
    if (r.keyringB64 && r.keyringB64 !== keyringB64) {
        await persistUpdatedKeyring(record.seedKid, r.keyringB64)
    }
    return { success: r.success, requestIds: r.requestIds, results: r.results }
}

export interface AdminListSubscriptionsOpArgs {
    planId?: string
    status?: string
    limit?: number
    offset?: number
}

export async function adminListSubscriptions(
    args: AdminListSubscriptionsOpArgs = {}
): Promise<Array<Record<string, any>>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const payload = {
        PlanId: args.planId || '',
        Status: args.status || '',
        Limit: args.limit ?? 50,
        Offset: args.offset ?? 0,
    }
    const r = await wasm.adminListSubscriptions(endpoint(), seedHex, JSON.stringify(payload))
    return r.items
}

export async function adminProvisionSubscription(
    userId: string,
    planId: string,
    quantity: number = 1,
): Promise<{
    success: boolean
    error: string
    subscription?: Record<string, any>
    licenses?: Array<Record<string, any>>
}> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.adminProvisionSubscription(endpoint(), seedHex, userId, planId, quantity)
}

export async function adminAssignLicense(
    licenseId: string,
    userId: string
): Promise<{ success: boolean; error: string; license?: Record<string, any> }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.adminAssignLicense(endpoint(), seedHex, licenseId, userId)
}

export async function adminUnassignLicense(
    licenseId: string
): Promise<{ success: boolean; error: string; license?: Record<string, any> }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.adminUnassignLicense(endpoint(), seedHex, licenseId)
}

export async function adminSearchUsers(
    searchTerm: string,
    limit: number = 10
): Promise<Array<{ id: string; email: string; name: string }>> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    const r = await wasm.adminSearchUsers(endpoint(), seedHex, searchTerm, limit)
    return r.items
}

export interface IdentityDetail {
    id: string
    kind: string // "user" | "team" | ""
    name: string
    email: string // users only
}

// Admin-only: resolve an identity id to its display detail (a user's name +
// email, or a team's name). Surfaces email, which the public getIdentity omits.
export async function adminGetIdentityDetail(id: string): Promise<IdentityDetail> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return (await wasm.adminGetIdentityDetail(endpoint(), seedHex, id)) as IdentityDetail
}

export async function adminDeleteSubscription(
    subscriptionId: string
): Promise<{ success: boolean; error: string }> {
    const { seedHex } = await prepareDeviceArgs()
    const wasm = await loadPracticemdWasm()
    return await wasm.adminDeleteSubscription(endpoint(), seedHex, subscriptionId)
}

// --- Helpers ---

function uint8ArrayToBase64(bytes: Uint8Array): string {
    let s = ''
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
    return btoa(s)
}

function base64ToUint8Array(b64: string): Uint8Array {
    if (!b64) return new Uint8Array(0)
    const bin = atob(b64)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    return out
}

function base64ToHex(b64: string): string {
    const bin = atob(b64)
    let out = ''
    for (let i = 0; i < bin.length; i++) {
        out += bin.charCodeAt(i).toString(16).padStart(2, '0')
    }
    return out
}

function uint8ArrayToHex(bytes: Uint8Array): string {
    let out = ''
    for (let i = 0; i < bytes.length; i++) {
        out += bytes[i].toString(16).padStart(2, '0')
    }
    return out
}

// base64url (RFC 4648 §5) uses '-' and '_' instead of '+' and '/',
// and typically omits padding. Convert to standard base64 before atob.
function base64urlToUint8Array(s: string): Uint8Array {
    let b64 = s.replace(/-/g, '+').replace(/_/g, '/')
    const padMod = b64.length % 4
    if (padMod === 2) b64 += '=='
    else if (padMod === 3) b64 += '='
    return base64ToUint8Array(b64)
}

// ---- Referrals (practicemd-api docs/Referrals-Architecture.md §9) ----
// The staff console, the clinician's referral page, and the signed acts a
// referrer takes once signed in. Every one is device-signed and refuses with
// the server's words, which reach a catch as a bare string: read them with
// wasmErrorMessage. The referrer's public pages are not here (publicApi.ts).

async function referralCall() {
    const { seedHex } = await prepareDeviceArgs()
    return { wasm: await loadPracticemdWasm(), at: endpoint(), seedHex }
}

export async function adminListReferrers(filter: ReferrerListFilter): Promise<ReferrerList> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminListReferrers(at, seedHex, JSON.stringify(filter || {}))
}

export async function adminGetReferrer(referrerId: string): Promise<ReferrerDetail> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminGetReferrer(at, seedHex, referrerId)
}

export async function adminCreateReferrer(input: ReferrerInput): Promise<CreatedReferrer> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminCreateReferrer(at, seedHex, JSON.stringify(input))
}

export async function adminUpdateReferrer(update: ReferrerUpdate): Promise<ReferrerRecord> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminUpdateReferrer(at, seedHex, JSON.stringify(update))
}

export async function adminCreateReferralPair(
    referrerId: string,
    targetKind: 'provider' | 'practice',
    targetId: string,
    rank: number | null
): Promise<ReferralPair> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminCreateReferralPair(at, seedHex, referrerId, targetKind, targetId, rank)
}

export async function adminSetReferralRank(linkId: string, rank: number | null): Promise<void> {
    const { wasm, at, seedHex } = await referralCall()
    await wasm.adminSetReferralRank(at, seedHex, linkId, rank)
}

export async function adminRevokeReferralLink(linkId: string): Promise<void> {
    const { wasm, at, seedHex } = await referralCall()
    await wasm.adminRevokeReferralLink(at, seedHex, linkId)
}

export async function adminDeleteReferrer(referrerId: string): Promise<void> {
    const { wasm, at, seedHex } = await referralCall()
    await wasm.adminDeleteReferrer(at, seedHex, referrerId)
}

export async function adminRecordReferrerTouch(touch: ReferrerTouchInput): Promise<ReferralPair> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminRecordReferrerTouch(at, seedHex, JSON.stringify(touch))
}

/** profileId names the listed profile when status is "listed", else "". */
export async function adminSetReferrerRecruiting(referrerId: string, status: string, profileId: string): Promise<ReferrerRecord> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminSetReferrerRecruiting(at, seedHex, referrerId, status, profileId || '')
}

export async function adminListReferralClinicians(filter: ReferralClinicianFilter): Promise<ReferralClinicianList> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminListReferralClinicians(at, seedHex, JSON.stringify(filter || {}))
}

export async function adminGetReferralClinician(profileId: string, view: string): Promise<ReferralClinicianDetail> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminGetReferralClinician(at, seedHex, profileId, view || '')
}

/** The Clinicians tab (§9.1, P2-Q6): practices as rows with their members,
 *  and clinicians with no practice. The pair picker reads it too (P2-Q8). */
export async function adminListReferralTargets(filter: ReferralClinicianFilter): Promise<ReferralTargetList> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminListReferralTargets(at, seedHex, JSON.stringify(filter || {}))
}

/** The practice's matrix (P2-Q6 D). */
export async function adminGetReferralPractice(query: ReferralPracticeMatrixQuery): Promise<ReferralPracticeMatrix> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminGetReferralPractice(at, seedHex, JSON.stringify(query))
}

export async function getReferralPage(): Promise<ReferralPage> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.getReferralPage(at, seedHex)
}

/** Only the fields present change. */
export async function revokeReferralPair(linkId: string): Promise<void> {
    const { wasm, at, seedHex } = await referralCall()
    await wasm.revokeReferralPair(at, seedHex, linkId)
}

export async function referralShareAs(slug: string): Promise<ReferralShareAs> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.referralShareAs(at, seedHex, slug)
}

/** asKind: 'me' | 'team' | 'profile'; asId: the team's or profile's id ('' for me). */
export async function shareReferralCode(slug: string, asKind: string, asId: string): Promise<SharedReferralCode> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.shareReferralCode(at, seedHex, slug, asKind, asId || '')
}

/** False when the secret was spent already or is unknown. */
export async function attachSharedCodes(holderSecret: string): Promise<boolean> {
    const { wasm, at, seedHex } = await referralCall()
    return !!(await wasm.attachSharedCodes(at, seedHex, holderSecret)).attached
}

export async function adminMakeReferralCode(linkId: string): Promise<MadeReferralCode> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminMakeReferralCode(at, seedHex, linkId)
}

export async function adminFindAccounts(query: string): Promise<ReferrerAccountCandidate[]> {
    const { wasm, at, seedHex } = await referralCall()
    return (await wasm.adminFindAccounts(at, seedHex, query)).accounts || []
}

/** Link to accountId, or unlink (accountId ''); the linked account, null on unlink. */
export async function adminLinkReferrerAccount(referrerId: string, accountId: string, unlink: boolean): Promise<ReferrerAccount | null> {
    const { wasm, at, seedHex } = await referralCall()
    return wasm.adminLinkReferrerAccount(at, seedHex, referrerId, accountId || '', unlink)
}
